import { canonicalizeSduiDocument } from "../contract/canonicalize.js";
import {
  globalElementNamesForTree,
  pageReferencedGlobalTemplates,
} from "../contract/catalog.js";
import { buildFrameShell, createPageCanvas, PAGE_FRAMES } from "../dom/canvas.js";
import {
  elementStorageKey,
  elementTreeKey,
  isStateNodeKey,
  isWidgetElementTreeKey,
  isWidgetGroupTreeKey,
  isWidgetVisualTreeKey,
  chromeFrameWidgetKey,
  chromePageSectionKey,
  chromePageStorageKey,
  chromePartSectionKey,
  chromeStorageKey,
  isChromeTreeKey,
  parseChromeKey,
  parseElementKey,
  parseStateKey,
  parseWidgetKey,
  parseWidgetTreeKey,
  stateStorageKey,
  widgetElementTreeKey,
  widgetElementsFolderKey,
  widgetGroupFolderKey,
  widgetGroupTreeKey,
  widgetStorageKey,
  widgetVisualTreeKey,
} from "../selection/keys.js";
import { planComponentPreview } from "../selection/preview-plan.js";
import { buildTokenContext, isColorValue, resolveColor } from "../contract/tokens.js";
import {
  documentRoot,
  mergePageVars,
  getChrome,
  getDataset,
  getGlobalElements,
  getPages,
  getWidgets,
  isChromeWidgetSurface,
  listChromeFrameRefs,
  listFrameWidgets,
  framePlacement,
  frameRootContract,
  paletteIndexByName,
  listWidgetCatalogNames,
  getSurfaceCard,
  getViewFromCard,
  listComponentCardOptions,
  listPageKeys,
  catalogNameForChromePart,
} from "../contract/document.js";
import {
  defaultWidgetPreviewMode,
  listDatasetRecords,
  listWidgetDatasetKinds,
  widgetHasListPhasePreview,
  widgetListPreviewModes,
  listWidgetPreviewBarEntries,
  interfacePhaseTitle,
  widgetPreviewDatasetKind,
} from "../contract/widgets.js";
import { resolveBreakpoint, resolvePreviewBreakpoint } from "../contract/breakpoints.js";
import {
  createSpriteSheet,
  nodeTitle,
  renderNode,
  renderResolvedNode,
  setAdminImageBaseUrl,
} from "../dom/paint.js";
import { renderDocument, renderPageFrame } from "../preview/compose.js";
import { buildBreakpointVisibilityHint } from "../preview/breakpoint-hint.js";
import {
  mergePreviewTranslations,
  setPreviewLocale,
} from "../preview/localization.js";
import {
  createPaneWidthController,
  initPaneResize,
  setPropsPaneCollapsed as setPropsPaneCollapsedImpl,
  setTreePaneCollapsed as setTreePaneCollapsedImpl,
  togglePane as togglePaneImpl,
} from "./panes/index.js";
import { documentPathForKey } from "../selection/document-path.js";
import { findPhase, visualChildren, walkVisual } from "../selection/phase-node.js";
import {
  initPropertiesInspector,
  resolvePropertiesInspectKey,
  updatePropertiesInspector,
} from "./properties/inspector.js";
import { initTokensPanel, updateTokensPanel } from "./tokens/panel.js";
import { createSession } from "./session.ts";
import { createPageStage } from "./page-stage/index.js";
import { createComponentStage } from "./component-stage/index.js";
import { createEditor } from "./editor/index.js";
import { createStructure } from "./structure/index.js";
import { createSource } from "./source/index.js";

const session = createSession();
session.viewMode = "page";
session.selectedKey = "header:state";

let paintPage = () => {};
let paintComponent = (_options = {}) => {};
let syncEditorToDoc = (_options = {}) => {};
let buildTree = (_doc) => {};
let loadSample = async () => {};
/** @type {(text: string) => { ok: true, value: unknown } | { ok: false, error: Error }} */
let parseEditorJson = () => ({ ok: false, error: new Error("editor not ready") });

const source = document.querySelector("#source");
const sourceSwatches = document.querySelector("#source-swatches");
const errorBox = document.querySelector("#error");
const contractIssuesEl = document.querySelector("#contract-issues");
const preview = document.querySelector("#preview");
const tree = document.querySelector("#tree");
const meta = document.querySelector("#meta");
const actions = document.querySelector("#actions");
const previewBlock = document.querySelector("#preview-block");
const frame = document.querySelector("#frame");
function getPreviewBreakpointHintEl() {
  let el = document.querySelector("#preview-breakpoint-hint");
  if (!el && frame) {
    el = document.createElement("div");
    el.id = "preview-breakpoint-hint";
    el.className = "preview-breakpoint-hint";
    el.hidden = true;
    if (preview) frame.insertBefore(el, preview);
    else frame.append(el);
  }
  return el;
}
const frameBgInput = document.querySelector("#frame-bg");
const viewport = document.querySelector("#viewport");
const previewLocaleSelect = document.querySelector("#preview-locale");
const FRAME_BG_STORAGE_KEY = "header-viewer-frame-bg";
const PREVIEW_LOCALE_STORAGE_KEY = "slon-bff-preview-locale";
const DEFAULT_FRAME_BG = "#d8dce3";
const DEFAULT_PREVIEW_LOCALE = "ru";
const PREVIEW_LOCALE_VALUES = new Set(["ru", "en", "ar", "es", "zh", "de", "fr", "hi", "ja"]);
const THEME_VALUES = new Set(["Dark", "Light"]);
let themeValue = "Dark";
const themeButtons = document.querySelectorAll(".theme-switch__btn[data-theme]");

function getTheme() {
  return themeValue;
}

function setTheme(next) {
  if (!THEME_VALUES.has(next)) return;
  themeValue = next;
  for (const button of themeButtons) {
    const active = button.dataset.theme === next;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", String(active));
  }
}
/** Component preview chrome/page surface (tree selection; no card dropdown). */
let componentPreviewSurface = "";
const pageSelect = document.querySelector("#page-select");
const pageSelectWrap = document.querySelector("#page-select-wrap");
const workspaceSelect = document.querySelector("#workspace-select");
const workspaceSelectWrap = document.querySelector("#workspace-select-wrap");
const localExamplesToggle = document.querySelector("#local-examples-toggle");
const workspaceRoot = document.querySelector("main.workspace");
const componentWorkspace = document.querySelector("#component-workspace");
const treePane = document.querySelector(".pane--tree");
const treePaneToggle = document.querySelector('.pane__toggle[data-pane="tree"]');
const propsPane = document.querySelector(".pane--props");
const propsPaneToggle = document.querySelector('.pane__toggle[data-pane="props"]');
const propertiesInspector = document.querySelector("#properties-inspector");
const componentStage = document.querySelector("#component-stage");
const widgetPreviewBar = document.querySelector("#widget-preview-bar");
const widgetDatasetSelect = document.querySelector("#widget-dataset");
const WIDGET_DATASET_STORAGE_KEY = "header-viewer-widget-dataset";
const WIDGET_PREVIEW_MODE_STORAGE_KEY = "header-viewer-widget-preview-mode";
const widgetPreviewModeButtons = document.querySelectorAll("[data-widget-preview-mode]");
const widgetBehaviorWrap = document.querySelector("#widget-behavior-wrap");
const widgetBehaviorInput = document.querySelector("#widget-behavior");
const WIDGET_GROUP_SCREEN_STORAGE_KEY = "header-viewer-widget-group-screen";
const pageWorkspace = document.querySelector("#page-workspace");
const pageMeta = document.querySelector("#page-meta");
const tokensWorkspace = document.querySelector("#tokens-workspace");
const tokensPanelRoot = document.querySelector("#tokens-panel-root");
const saveNodeButton = document.querySelector("#save-node");
const modeButtons = document.querySelectorAll(".mode-switch__btn");

const bootParams = new URLSearchParams(window.location.search);
const CUSTOM_CONTRACT_API = bootParams.get("contractApi");
/** `contractApi=file:example.json` is a workspace id, not a reason to hide the picker. */
const CONTRACT_FILE_SOURCE =
  CUSTOM_CONTRACT_API && /^file:[^/\\]+\.json$/i.test(CUSTOM_CONTRACT_API)
    ? CUSTOM_CONTRACT_API
    : null;
const USE_ADMIN_CONTRACT = !CUSTOM_CONTRACT_API || Boolean(CONTRACT_FILE_SOURCE);
const WORKSPACE_STORAGE_KEY = "bff-viewer-workspace-id";
const WORKSPACE_META_CACHE_KEY = "bff-viewer-workspace-meta";
const LOCAL_EXAMPLES_PREF_KEY = "bff-viewer-local-examples";
const DEFAULT_WORKSPACE_ID = "1";

/** @type {{ localExamplesAllowed: boolean, adminApiBaseUrl?: string, adminConfigured?: boolean }} */
let viewerConfig = { localExamplesAllowed: false, adminApiBaseUrl: "", adminConfigured: false };

function readLocalExamplesPref() {
  try {
    return localStorage.getItem(LOCAL_EXAMPLES_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

let localExamplesPref = readLocalExamplesPref();

function setLocalExamplesPref(enabled) {
  localExamplesPref = enabled;
  try {
    if (enabled) localStorage.setItem(LOCAL_EXAMPLES_PREF_KEY, "1");
    else localStorage.removeItem(LOCAL_EXAMPLES_PREF_KEY);
  } catch {
    /* private mode */
  }
  syncLocalExamplesToggleUi();
}

function applyLocalExamplesBootParam() {
  const param = bootParams.get("localExamples");
  if (param === "1") setLocalExamplesPref(true);
  else if (param === "0") setLocalExamplesPref(false);
}

applyLocalExamplesBootParam();

function isLocalExamplesEnabled() {
  return viewerConfig.localExamplesAllowed && localExamplesPref;
}

async function refreshViewerConfig() {
  try {
    const response = await fetch("/api/viewer-config", { cache: "no-store" });
    if (response.ok) {
      const data = await response.json();
      const adminApiBaseUrl = typeof data.adminApiBaseUrl === "string" ? data.adminApiBaseUrl : "";
      viewerConfig = {
        localExamplesAllowed: Boolean(data.localExamplesAllowed),
        adminApiBaseUrl,
        adminConfigured: Boolean(data.adminConfigured),
      };
      setAdminImageBaseUrl(adminApiBaseUrl);
    } else {
      const probe = await fetch("/api/example-files", { cache: "no-store" });
      if (probe.ok) {
        const data = await probe.json();
        viewerConfig = {
          localExamplesAllowed: data.localExamplesAllowed !== false && Array.isArray(data.files),
          adminApiBaseUrl: "",
          adminConfigured: false,
        };
      }
    }
  } catch {
    /* offline / static hosting */
  }
  syncLocalExamplesToggleUi();
}

function isPreviewLocale(value) {
  return typeof value === "string" && PREVIEW_LOCALE_VALUES.has(value.trim().toLowerCase());
}

function readStoredPreviewLocale() {
  try {
    const stored = localStorage.getItem(PREVIEW_LOCALE_STORAGE_KEY);
    if (isPreviewLocale(stored)) return stored.trim().toLowerCase();
  } catch {
    /* private mode */
  }
  return "";
}

function persistPreviewLocale(locale) {
  try {
    localStorage.setItem(PREVIEW_LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode */
  }
}

function applyPreviewLocale(locale, { repaint = false, syncUrl = false } = {}) {
  const next = isPreviewLocale(locale) ? locale.trim().toLowerCase() : DEFAULT_PREVIEW_LOCALE;
  setPreviewLocale(next);
  if (previewLocaleSelect && previewLocaleSelect.value !== next) {
    previewLocaleSelect.value = next;
  }
  persistPreviewLocale(next);
  if (syncUrl) syncUrlFromAppState();
  if (repaint) paint({ refreshEditor: false });
  return next;
}

function initPreviewLocale() {
  const fromUrl = bootParams.get("lang");
  const initial = isPreviewLocale(fromUrl)
    ? fromUrl.trim().toLowerCase()
    : (readStoredPreviewLocale() || DEFAULT_PREVIEW_LOCALE);
  applyPreviewLocale(initial, { repaint: false, syncUrl: false });
  if (previewLocaleSelect) {
    previewLocaleSelect.addEventListener("change", () => {
      applyPreviewLocale(previewLocaleSelect.value, { repaint: true, syncUrl: true });
    });
  }
}

/** Load LocalizationDictionary catalog from AdminApp via BFF proxy. */
async function loadAdminTranslationsCatalog() {
  if (!viewerConfig.adminConfigured) return;
  try {
    const response = await fetch("/api/admin-translations", { cache: "no-store" });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      console.warn(`admin-translations ${response.status}: ${detail.slice(0, 200)}`);
      return;
    }
    const data = await response.json();
    if (data?.entries && typeof data.entries === "object") {
      mergePreviewTranslations(data.entries);
    }
  } catch (error) {
    console.warn("admin-translations unavailable", error);
  }
}

function syncLocalExamplesToggleUi() {
  if (!localExamplesToggle) return;
  if (!viewerConfig.localExamplesAllowed) {
    localExamplesToggle.hidden = true;
    return;
  }
  localExamplesToggle.hidden = false;
  const active = isLocalExamplesEnabled();
  localExamplesToggle.setAttribute("aria-pressed", String(active));
  localExamplesToggle.classList.toggle("is-active", active);
  localExamplesToggle.title = active
    ? "Скрыть JSON из example/"
    : "Показать JSON из example/";
}

let loadSampleGeneration = 0;

function isAdminWorkspaceId(id) {
  return /^\d+$/.test(id);
}

function isLocalFileSource(id) {
  return /^file:[^/\\]+\.json$/i.test(id);
}

function isValidSourceId(id) {
  return isAdminWorkspaceId(id) || isLocalFileSource(id);
}

function localFileNameFromSource(id) {
  return isLocalFileSource(id) ? id.slice("file:".length) : "";
}

function exampleFileFromSourceId(id) {
  return isLocalFileSource(id) ? localFileNameFromSource(id) : null;
}

/** Active example/ file when workspace or contractApi uses file:*.json */
function activeExampleFileName() {
  return (
    exampleFileFromSourceId(selectedWorkspaceId)
    ?? (CUSTOM_CONTRACT_API ? exampleFileFromSourceId(CUSTOM_CONTRACT_API) : null)
  );
}

function readInitialWorkspaceId() {
  if (CONTRACT_FILE_SOURCE) {
    setLocalExamplesPref(true);
    return CONTRACT_FILE_SOURCE;
  }
  const fromUrl = bootParams.get("workspace");
  if (fromUrl && isValidSourceId(fromUrl)) {
    if (isLocalFileSource(fromUrl) && !localExamplesPref) return DEFAULT_WORKSPACE_ID;
    return fromUrl;
  }
  try {
    const stored = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (stored && isValidSourceId(stored)) {
      if (isLocalFileSource(stored) && !localExamplesPref) return DEFAULT_WORKSPACE_ID;
      return stored;
    }
  } catch {
    /* private mode */
  }
  return DEFAULT_WORKSPACE_ID;
}

let selectedWorkspaceId = readInitialWorkspaceId();

function contractApiUrl() {
  if (CUSTOM_CONTRACT_API) {
    const exampleFile = exampleFileFromSourceId(CUSTOM_CONTRACT_API);
    if (exampleFile) {
      return `/api/example-contract?file=${encodeURIComponent(exampleFile)}`;
    }
    return CUSTOM_CONTRACT_API;
  }
  const exampleFile = exampleFileFromSourceId(selectedWorkspaceId);
  if (exampleFile) {
    return `/api/example-contract?file=${encodeURIComponent(exampleFile)}`;
  }
  return `/api/sdui-contract?workspace=${encodeURIComponent(selectedWorkspaceId)}`;
}
const pageCanvas = createPageCanvas();
pageWorkspace.prepend(pageCanvas.root);

let viewMode = "page";
let widgetDatasetSelection = loadWidgetDatasetSelection();
let widgetPreviewModeSelection = loadWidgetPreviewModeSelection();
let widgetGroupActiveScreen = loadWidgetGroupActiveScreen();
const previewAuthForm = { phone: "", email: "", password: "", country: "LV" };
let previewAuthFormErrors = {};
let widgetBehaviorMode = false;

let doc = null;
let docFileName = "admin-contract.json";
/** @type {"api" | "file"} */
let docSource = "api";
/** @type {FileSystemFileHandle | null} */
let docFileHandle = null;

let rendered = null;
let nodeSaveInFlight = false;
let selectedKey = "header:state";
/** What the stage draws. Canvas hits update `selectedKey` only. */
let previewScopeKey = selectedKey;
let timer = 0;
let editorProgrammatic = false;
/** Selected tree key last loaded into `#source` (do not overwrite while editing same node). */
let editorLoadedKey = null;
let treeTooltipTimer = 0;
let treeTooltipEl = null;
const CHROME_SURFACES_WITH_ELEMENTS = [];
const treeCollapsed = new Set();
let treeDefaultsApplied = false;
const TREE_CHEVRON_PATH = "M2.697 5.03a.9.9 0 0 1 1.273 0L8 9.06l4.03-4.03a.9.9 0 1 1 1.273 1.273L8.637 10.97a.9.9 0 0 1-1.273 0L2.697 6.303a.9.9 0 0 1 0-1.273";

const TREE_TOOLTIP_DELAY_MS = 400;
const VALID_VIEW_MODES = new Set(["component", "page", "tokens"]);
let urlSyncTimer = 0;
const EDITOR_WIDTH_STORAGE_KEY = "header-viewer-editor-width";
const EDITOR_WIDTH_DEFAULT = 480;
const EDITOR_WIDTH_MIN = 224;
const EDITOR_WIDTH_MAX = 900;

const treePaneSize = createPaneWidthController({
  storageKey: "header-viewer-tree-width",
  cssVar: "--col-tree",
  hiddenClass: "is-tree-hidden",
  defaultWidth: 260,
  minWidth: 180,
  maxWidth: 560,
  workspaceEl: componentWorkspace,
});

const propsPaneSize = createPaneWidthController({
  storageKey: "header-viewer-props-width",
  cssVar: "--col-props",
  hiddenClass: "is-props-hidden",
  defaultWidth: 280,
  minWidth: 200,
  maxWidth: 480,
  workspaceEl: componentWorkspace,
});

const editorPaneSize = createPaneWidthController({
  storageKey: EDITOR_WIDTH_STORAGE_KEY,
  cssVar: "--col-editor",
  hiddenClass: "is-editor-hidden",
  defaultWidth: EDITOR_WIDTH_DEFAULT,
  minWidth: EDITOR_WIDTH_MIN,
  maxWidth: EDITOR_WIDTH_MAX,
  workspaceEl: componentWorkspace,
});
const EDITOR_PADDING_TOP = 12;
const EDITOR_PADDING_LEFT = 14;
const EDITOR_LINE_HEIGHT = 18;
const SWATCH_SIZE = 14;
const SWATCH_BEFORE_TEXT_GAP = 4;

let measureEl = null;

source.addEventListener("input", () => {
  if (editorProgrammatic) return;
  updateEditorSwatches();
  clearTimeout(timer);
  timer = setTimeout(syncEditorToDoc, 180);
  clearTimeout(contractCheckTimer);
  contractCheckTimer = setTimeout(() => { refreshEditorContractCheck(); }, 280);
});

source.addEventListener("scroll", updateEditorSwatches);

source.addEventListener("blur", () => {
  if (editorProgrammatic || !doc) return;
  clearTimeout(timer);
  syncEditorToDoc({ showErrors: true, formatEditor: true });
});

document.querySelector("#reload").addEventListener("click", () => reloadDoc());
document.querySelector("#copy-json").addEventListener("click", copyJson);
if (saveNodeButton) {
  saveNodeButton.addEventListener("click", () => { saveSelectedNodeToDraft(); });
}
viewport.addEventListener("change", applyViewport);
initPreviewLocale();
if (frameBgInput) {
  const persistFrameBackground = () => {
    const color = frameBgInput.value;
    applyFrameBackground(color);
    try {
      localStorage.setItem(FRAME_BG_STORAGE_KEY, color);
    } catch {
      /* ignore quota / private mode */
    }
  };
  frameBgInput.addEventListener("input", persistFrameBackground);
  frameBgInput.addEventListener("change", persistFrameBackground);
  restoreFrameBackground();
}
new ResizeObserver(() => {
  if (viewport.value === "fluid") paint();
}).observe(frame);
for (const button of themeButtons) {
  button.addEventListener("click", () => {
    const next = button.dataset.theme;
    if (!next || next === themeValue) return;
    setTheme(next);
    paint();
    updateEditorSwatches();
    syncUrlFromAppState();
  });
}
for (const button of modeButtons) {
  button.addEventListener("click", () => setViewMode(button.dataset.mode));
}
if (pageSelect) {
  pageSelect.addEventListener("change", () => {
    paint();
    syncUrlFromAppState();
  });
}
if (widgetDatasetSelect) {
  widgetDatasetSelect.addEventListener("change", () => {
    const widgetName = parseWidgetKey(selectedKey);
    if (!widgetName) return;
    setWidgetDatasetKind(widgetName, widgetDatasetSelect.value);
    syncWidgetPreviewBar();
    refreshPropertiesInspector();
    paint({ refreshEditor: false });
    syncUrlFromAppState();
  });
}
document.querySelector(".widget-preview-mode")?.addEventListener("click", (event) => {
  const button = event.target.closest("[data-widget-preview-mode]");
  if (!button) return;
  const widgetName = parseWidgetKey(selectedKey);
  if (!widgetName) return;
  const mode = button.dataset.widgetPreviewMode;
  if (!mode) return;
  setWidgetPreviewMode(widgetName, mode);
  syncWidgetPreviewBar();
  paint({ refreshEditor: false });
  syncUrlFromAppState();
});
if (widgetBehaviorInput) {
  widgetBehaviorInput.addEventListener("change", () => {
    widgetBehaviorMode = widgetBehaviorInput.checked;
    paint({ refreshEditor: false });
  });
}
preview.addEventListener("click", handlePreviewCanvasClick, true);
pageCanvas.row.addEventListener("click", selectFromCanvas, true);
tree.addEventListener("scroll", hideTreeTooltip, true);
applyBootUrlParams();
applyViewModeUi(viewMode);
applyViewport();

for (const button of document.querySelectorAll(".pane__toggle")) {
  button.addEventListener("click", () => togglePane(button));
}

initPaneResize(".pane-resize--tree", treePaneSize, componentWorkspace);
initPaneResize(".pane-resize--props", propsPaneSize, componentWorkspace);
initPaneResize(".pane-resize--editor", editorPaneSize, componentWorkspace);
treePaneSize.apply();
propsPaneSize.apply();
editorPaneSize.apply();
initPropertiesInspector(propertiesInspector);
if (tokensPanelRoot) {
  initTokensPanel(tokensPanelRoot, {
    getTheme,
    onCommit: () => {
      paint({ refreshEditor: false });
      updateNodeSaveButton();
    },
  });
}

function setTreePaneCollapsed(collapsed) {
  setTreePaneCollapsedImpl(collapsed, treePane, treePaneToggle, componentWorkspace, treePaneSize);
}

function setPropsPaneCollapsed(collapsed) {
  setPropsPaneCollapsedImpl(collapsed, propsPane, propsPaneToggle, componentWorkspace, propsPaneSize);
}

function togglePane(button) {
  togglePaneImpl(
    button,
    componentWorkspace,
    treePaneSize,
    propsPaneSize,
    editorPaneSize,
    setTreePaneCollapsed,
    setPropsPaneCollapsed,
  );
}

function refreshPropertiesInspector() {
  updatePropertiesInspector({
    doc,
    selectedKey,
    resolveNode: (key) => resolveNodeByKey(key, doc),
    breakpoint: rendered?.breakpoint,
    theme: getTheme(),
    surface: activeSurface(),
    phase: phaseContextForSelection(selectedKey),
    widgetPreviewPhase: previewPhaseId(parseWidgetKey(selectedKey)),
    onSelectKey: (key) => {
      selectedKey = key;
      expandTreeToKey(selectedKey);
      markTree();
      scrollTreeToSelected();
      clearHoverHighlight();
      highlight(selectedKey);
      showSelectedInEditor();
      refreshPropertiesInspector();
      syncUrlFromAppState();
    },
    onHoverKey: (key) => {
      if (key) highlightHover(key);
      else {
        clearHoverHighlight();
        highlight(selectedKey);
      }
    },
    onCommit: () => {
      showSelectedInEditor();
      paint({ refreshEditor: false });
      updateNodeSaveButton();
    },
    datasetState: currentDatasetState(),
    onDatasetState: (state) => {
      const widgetName = parseWidgetKey(selectedKey);
      if (!widgetName || !doc) return;
      const kind = stateDatasetKind(widgetName);
      if (!applyFirstRecordState(kind, state)) return;
      paint({ refreshEditor: false });
      void persistDatasetSlice(kind);
    },
  });
}

function documentPathContext() {
  return {
    activeSurface: activeSurface(),
    getWidgetGroupScreenId: (widgetName, spec) => getWidgetGroupScreenId(widgetName, spec),
    phaseIdForWidget: (widgetName) => previewPhaseId(widgetName),
  };
}

function previewPhaseId(widgetName) {
  if (!doc || !widgetName) return null;
  const mode = getWidgetPreviewMode(widgetName, doc);
  return mode && mode !== "data" ? mode : null;
}

function phaseItemLabel(item, index) {
  if (!item || typeof item !== "object") return String(index);
  const own = item.appearance?.text;
  if (typeof own === "string" && own) return own;
  const nested = Array.isArray(item.items)
    ? item.items.find((child) => typeof child?.appearance?.text === "string")
    : null;
  const text = nested?.appearance?.text;
  if (typeof item.id === "string" && typeof text === "string") return `${item.id} · ${text}`;
  if (typeof item.id === "string") return item.id;
  return text || item.type || String(index);
}

function phaseContextForSelection(key) {
  const parsed = parseWidgetTreeKey(key);
  if (!parsed?.name || parsed.section !== "visual" || !doc) return null;
  const spec = getWidgets(doc)[parsed.name];
  const match = findPhase(spec, previewPhaseId(parsed.name));
  if (!match) return null;
  const walked = walkVisual(spec, parsed.indices, match.entry.id);
  const phaseId = String(match.entry.id);
  const phaseTitle = interfacePhaseTitle(spec, phaseId) ?? phaseId;
  if (walked.inPhase) return { id: phaseId, title: phaseTitle, items: null };
  if (walked.node?.id !== match.entry.container) return null;
  const items = Array.isArray(match.entry.items) ? match.entry.items : [];
  return {
    id: phaseId,
    title: phaseTitle,
    items: items.map((item, index) => ({
      key: widgetVisualTreeKey(parsed.name, [...parsed.indices, index]),
      label: phaseItemLabel(item, index),
    })),
  };
}

function currentAdminWorkspaceItem() {
  if (!isAdminWorkspaceId(selectedWorkspaceId)) return null;
  return workspaceCatalog.find((item) => item.id === selectedWorkspaceId && item.kind === "admin") ?? null;
}

function updateWorkspaceVersionInCatalog(workspaceId, version) {
  const index = workspaceCatalog.findIndex((item) => item.id === workspaceId);
  if (index === -1) return;
  workspaceCatalog[index] = { ...workspaceCatalog[index], version };
  writeWorkspaceMetaCache(workspaceCatalog.filter((item) => item.kind === "admin"));
  if (workspaceSelect?.value === workspaceId) {
    const option = workspaceSelect.querySelector(`option[value="${CSS.escape(workspaceId)}"]`);
    if (option) option.textContent = workspaceOptionLabel(workspaceCatalog[index]);
  }
}

let contractCheckGeneration = 0;
let contractCheckTimer = null;

function appendContractIssuesGroup(title, result) {
  if (!contractIssuesEl || result == null) return;
  const section = document.createElement("section");
  section.className = "contract-issues__group";
  const heading = document.createElement("h3");
  heading.className = "contract-issues__title";
  heading.textContent = title;
  section.appendChild(heading);
  if (result.ok) {
    const ok = document.createElement("p");
    ok.className = "contract-issues__ok";
    ok.textContent = "Ошибок нет";
    section.appendChild(ok);
  } else if (!result.issues?.length) {
    const summary = document.createElement("p");
    summary.className = "contract-issues__summary contract-issues__summary--error";
    summary.textContent = "Есть ошибки контракта, но список пуст (обновите страницу или перезапустите serve).";
    section.appendChild(summary);
  }
  if (result.issues?.length) {
    const list = document.createElement("ul");
    list.className = "contract-issues__list";
    for (const issue of result.issues) {
      const item = document.createElement("li");
      item.className = "contract-issues__item";
      const pathEl = document.createElement("code");
      pathEl.className = "contract-issues__path";
      pathEl.textContent = issue.path || "/";
      item.append(pathEl, " — ", issue.message);
      if (issue.fix) {
        const fixEl = document.createElement("div");
        fixEl.className = "contract-issues__fix";
        fixEl.textContent = issue.fix;
        item.appendChild(fixEl);
      }
      list.appendChild(item);
    }
    section.appendChild(list);
    if (result.total > result.issues.length) {
      const more = document.createElement("p");
      more.className = "contract-issues__more";
      more.textContent = `… ещё ${result.total - result.issues.length} (показано ${result.issues.length})`;
      section.appendChild(more);
    }
  }
  contractIssuesEl.appendChild(section);
}

function renderContractIssuesGroup(title, result, contractError) {
  if (!contractIssuesEl) return;
  contractIssuesEl.replaceChildren();
  if (contractError) {
    contractIssuesEl.hidden = false;
    const summary = document.createElement("p");
    summary.className = "contract-issues__summary contract-issues__summary--error";
    summary.textContent = `Проверка контракта: ${contractError}`;
    contractIssuesEl.appendChild(summary);
    return;
  }
  if (!result) {
    contractIssuesEl.hidden = true;
    return;
  }
  contractIssuesEl.hidden = false;
  appendContractIssuesGroup(title, result);
}

function editorContractSpec(key) {
  if (!key || viewMode === "tokens") return null;
  const widget = parseWidgetTreeKey(key);
  if (widget) {
    if (!widget.section) {
      return { kind: "widget", title: `Виджет · ${widget.name}`, name: widget.name };
    }
    if (widget.section === "visual") {
      return { kind: "block", title: `Узел · ${widget.name}`, widgetName: widget.name };
    }
    return null;
  }
  if (parseElementKey(key)) {
    return { kind: "block", title: "Элемент · палитра" };
  }
  if (parseStateKey(key)) {
    return { kind: "block", title: "View" };
  }
  const chrome = parseChromeKey(key);
  if (chrome?.pageKey) {
    return { kind: "page", title: `Страница · ${chrome.pageKey}` };
  }
  return null;
}

function syncEditorToSelectionIfStale() {
  if (!doc || !selectedKey || editorEditingCurrentSelection()) return;
  if (document.activeElement === source) return;
  if (editorLoadedKey === selectedKey) return;
  const node = resolveNodeByKey(selectedKey, doc);
  if (node == null) return;
  formatEditorJson(node);
}

function editorContractPayload(key) {
  const parsed = parseEditorJson(source.value);
  if (parsed.ok) return { value: parsed.value };
  if (source.value.trim()) {
    return { error: `JSON: ${parsed.error.message}` };
  }
  const fromDoc = resolveNodeByKey(key, doc);
  if (fromDoc != null) return { value: fromDoc };
  return {};
}

async function refreshEditorContractCheck() {
  if (!contractIssuesEl || !doc) return;
  syncEditorToSelectionIfStale();
  const spec = editorContractSpec(selectedKey);
  if (!spec) {
    contractIssuesEl.hidden = true;
    contractIssuesEl.replaceChildren();
    return;
  }
  let payload;
  try {
    payload = editorContractPayload(selectedKey);
  } catch (error) {
    renderContractIssuesGroup(
      spec.title,
      null,
      error instanceof Error ? error.message : String(error),
    );
    return;
  }
  if (payload.error) {
    renderContractIssuesGroup(
      spec.title,
      { ok: false, issues: [{ path: "/", code: "json", message: payload.error }], total: 1 },
      null,
    );
    return;
  }
  if (payload.value === undefined) {
    contractIssuesEl.hidden = true;
    contractIssuesEl.replaceChildren();
    return;
  }
  const fragmentName = spec.kind === "widget" ? spec.name : spec.widgetName;
  const generation = ++contractCheckGeneration;
  try {
    const response = await fetch("/api/contract-fragment-check", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ kind: spec.kind, value: payload.value, name: fragmentName }),
    });
    const body = await response.json().catch(() => ({}));
    if (generation !== contractCheckGeneration) return;
    if (!response.ok) {
      renderContractIssuesGroup(
        spec.title,
        { ok: false, issues: [{ path: "/", code: "request", message: body.error ?? `HTTP ${response.status}` }], total: 1 },
        null,
      );
      return;
    }
    renderContractIssuesGroup(spec.title, body.result, null);
  } catch (error) {
    if (generation !== contractCheckGeneration) return;
    renderContractIssuesGroup(
      spec.title,
      null,
      error instanceof Error ? error.message : String(error),
    );
  }
}

function showNodeSaveFeedback() {
  if (!saveNodeButton) return;
  const previous = saveNodeButton.textContent;
  saveNodeButton.textContent = "Сохранено";
  saveNodeButton.classList.add("is-copied");
  setTimeout(() => {
    saveNodeButton.textContent = previous;
    saveNodeButton.classList.remove("is-copied");
  }, 1200);
}

function updateNodeSaveButton() {
  if (!saveNodeButton) return;
  const adminItem = currentAdminWorkspaceItem();
  const inspectKey = resolvePropertiesInspectKey(selectedKey, (key) => resolveNodeByKey(key, doc));
  const node = inspectKey ? resolveNodeByKey(inspectKey, doc) : null;
  const path = inspectKey && doc
    ? documentPathForKey(inspectKey, doc, documentPathContext())
    : null;
  const canSave = Boolean(
    adminItem
    && doc
    && inspectKey
    && node
    && path
    && !nodeSaveInFlight,
  );
  saveNodeButton.disabled = !canSave;
}

async function saveSelectedNodeToDraft() {
  if (!doc || nodeSaveInFlight) return;
  const workspaceItem = currentAdminWorkspaceItem();
  if (!workspaceItem) return;

  const inspectKey = resolvePropertiesInspectKey(selectedKey, (key) => resolveNodeByKey(key, doc));
  const node = inspectKey ? resolveNodeByKey(inspectKey, doc) : null;
  const path = inspectKey ? documentPathForKey(inspectKey, doc, documentPathContext()) : null;
  if (!inspectKey || !node || !path) {
    errorBox.hidden = false;
    errorBox.textContent = "Не удалось определить узел для сохранения.";
    return;
  }

  nodeSaveInFlight = true;
  updateNodeSaveButton();
  const previousLabel = saveNodeButton?.textContent;
  if (saveNodeButton) saveNodeButton.textContent = "Сохранение…";

  try {
    const response = await fetch("/api/sdui-node", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        workspaceId: Number(workspaceItem.id),
        path,
        value: node,
      }),
    });
    const envelope = await response.json().catch(() => ({}));
    if (!response.ok) {
      errorBox.hidden = false;
      errorBox.textContent = envelope.error ?? `Сохранение (${response.status})`;
      return;
    }
    errorBox.hidden = true;
    refreshEditorContractCheck();
    if (typeof envelope.version === "number") {
      updateWorkspaceVersionInCatalog(workspaceItem.id, envelope.version);
    }
    showNodeSaveFeedback();
  } catch (error) {
    errorBox.hidden = false;
    errorBox.textContent = `Сохранение: ${error.message}`;
  } finally {
    nodeSaveInFlight = false;
    if (saveNodeButton && saveNodeButton.textContent === "Сохранение…") {
      saveNodeButton.textContent = previousLabel ?? "Сохранить";
    }
    updateNodeSaveButton();
  }
}

/** @type {Array<{ id: string, name: string, version: number, kind: "admin" | "local" }>} */
let workspaceCatalog = [];

function readWorkspaceMetaCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(WORKSPACE_META_CACHE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeWorkspaceMetaCache(adminItems) {
  if (!adminItems.length) return;
  const cache = readWorkspaceMetaCache();
  for (const item of adminItems) {
    if (item.kind !== "admin") continue;
    cache[item.id] = { name: item.name, version: item.version };
  }
  try {
    localStorage.setItem(WORKSPACE_META_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* private mode */
  }
}

function normalizeWorkspaceItems(envelope) {
  const raw = envelope?.items ?? envelope?.Items;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      id: String(item.id ?? item.Id ?? ""),
      name: String(item.name ?? item.Name ?? "").trim(),
      version: Number(item.version ?? item.Version ?? 0),
      kind: "admin",
    }))
    .filter((item) => isAdminWorkspaceId(item.id));
}

function fallbackAdminItems(adminItems) {
  if (adminItems.length > 0) return adminItems;
  const cache = readWorkspaceMetaCache();
  const fromCache = Object.entries(cache)
    .filter(([id]) => isAdminWorkspaceId(id))
    .map(([id, meta]) => ({
      id,
      name: String(meta?.name ?? "").trim() || "черновик",
      version: Number(meta?.version ?? 0),
      kind: "admin",
    }));
  if (fromCache.length > 0) return fromCache;
  return [
    {
      id: DEFAULT_WORKSPACE_ID,
      name: "AdminApp · черновик",
      version: 0,
      kind: "admin",
    },
  ];
}

async function fetchExampleCatalog() {
  if (!viewerConfig.localExamplesAllowed) {
    return [];
  }
  const response = await fetch("/api/example-files", { cache: "no-store" });
  if (!response.ok) {
    return [];
  }
  const envelope = await response.json();
  if (envelope?.localExamplesAllowed === false) {
    return [];
  }
  const files = envelope?.files;
  if (!Array.isArray(files)) {
    return [];
  }
  return files
    .map((name) => String(name))
    .filter((name) => /^[^/\\]+\.json$/i.test(name))
    .map((name) => ({
      id: `file:${name}`,
      name,
      version: 0,
      kind: "local",
    }));
}

function workspaceOptionLabel(item) {
  if (item.kind === "local") {
    return item.name;
  }
  const name = item.name || "без имени";
  return item.version > 0 ? `#${item.id} · ${name} (v${item.version})` : `#${item.id} · ${name}`;
}

function appendWorkspaceOptions(group, items) {
  for (const item of items) {
    const option = document.createElement("option");
    option.value = item.id;
    option.textContent = workspaceOptionLabel(item);
    group.append(option);
  }
}

function renderWorkspaceSelectOptions(adminItems, localItems) {
  if (!workspaceSelect) return;
  workspaceSelect.replaceChildren();
  if (adminItems.length > 0) {
    const group = document.createElement("optgroup");
    group.label = "AdminApp · черновики";
    appendWorkspaceOptions(group, adminItems);
    workspaceSelect.append(group);
  }
  if (localItems.length > 0) {
    const group = document.createElement("optgroup");
    group.label = "Локально · example/";
    appendWorkspaceOptions(group, localItems);
    workspaceSelect.append(group);
  }
  workspaceSelect.disabled = false;
}

function ensureAdminSourceOption(adminItems, sourceId) {
  if (!isAdminWorkspaceId(sourceId)) {
    return adminItems;
  }
  if (adminItems.some((item) => item.id === sourceId)) {
    return adminItems;
  }
  const cached = readWorkspaceMetaCache()[sourceId];
  return [
    {
      id: sourceId,
      name: cached?.name || "черновик",
      version: Number(cached?.version ?? 0),
      kind: "admin",
    },
    ...adminItems,
  ];
}

function applyWorkspaceSelectState(adminItems, localItems) {
  const adminWithFallback = ensureAdminSourceOption(adminItems, selectedWorkspaceId);
  workspaceCatalog = [...adminWithFallback, ...localItems];
  if (workspaceCatalog.length === 0) {
    return false;
  }
  renderWorkspaceSelectOptions(adminWithFallback, localItems);
  if (!workspaceCatalog.some((item) => item.id === selectedWorkspaceId)) {
    selectedWorkspaceId = workspaceCatalog[0].id;
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, selectedWorkspaceId);
    } catch {
      /* private mode */
    }
  }
  workspaceSelect.value = selectedWorkspaceId;
  return true;
}

async function populateWorkspaceSelect() {
  await refreshViewerConfig();
  const localItems = await fetchExampleCatalog();

  let adminItems = [];
  let adminError = null;
  try {
    const response = await fetch("/api/customization-workspaces", { cache: "no-store" });
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`Список workspace (${response.status}): ${detail.slice(0, 300)}`);
    }
    const envelope = await response.json();
    adminItems = normalizeWorkspaceItems(envelope);
    writeWorkspaceMetaCache(adminItems);
  } catch (error) {
    adminError = error;
    console.warn("workspace list unavailable", adminError);
  }

  adminItems = fallbackAdminItems(adminItems);
  if (!applyWorkspaceSelectState(adminItems, localItems)) {
    throw new Error("Не удалось заполнить список источников.");
  }

  if (isLocalFileSource(selectedWorkspaceId) && !isLocalExamplesEnabled()) {
    selectedWorkspaceId = adminItems[0]?.id ?? DEFAULT_WORKSPACE_ID;
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, selectedWorkspaceId);
    } catch {
      /* private mode */
    }
    workspaceSelect.value = selectedWorkspaceId;
    loadSample().catch(showLoadError);
  } else if (!doc && isAdminWorkspaceId(selectedWorkspaceId)) {
    loadSample().catch(showLoadError);
  }

  return adminError;
}

async function toggleLocalExamplesSources() {
  if (!viewerConfig.localExamplesAllowed) return;
  setLocalExamplesPref(!localExamplesPref);
  if (isLocalFileSource(selectedWorkspaceId) && !isLocalExamplesEnabled()) {
    selectedWorkspaceId = DEFAULT_WORKSPACE_ID;
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, selectedWorkspaceId);
    } catch {
      /* private mode */
    }
  }
  try {
    await populateWorkspaceSelect();
    await loadSample();
    syncUrlFromAppState();
  } catch (error) {
    showLoadError(error);
  }
}

function dropFileContractApiFromUrl() {
  if (!CONTRACT_FILE_SOURCE) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("contractApi");
  url.searchParams.set("workspace", selectedWorkspaceId);
  history.replaceState(null, "", url);
}

async function bootstrapViewer() {
  dropFileContractApiFromUrl();
  await refreshViewerConfig();
  await loadAdminTranslationsCatalog();
  if (USE_ADMIN_CONTRACT && workspaceSelectWrap && workspaceSelect) {
    workspaceSelectWrap.hidden = false;
    try {
      const adminError = await populateWorkspaceSelect();
      if (adminError) {
        errorBox.hidden = false;
        errorBox.textContent =
          `${adminError.message} Показаны кэш или запасной workspace — проверьте AdminApp и SLON_ADMIN_API_BASE_URL / SLON_ADMIN_API_TOKEN.`;
      }
    } catch (error) {
      console.warn("workspace list unavailable", error);
      showLoadError(error);
    }
    workspaceSelect.addEventListener("change", () => {
      selectedWorkspaceId = workspaceSelect.value;
      if (isLocalFileSource(selectedWorkspaceId) && viewerConfig.localExamplesAllowed) {
        setLocalExamplesPref(true);
      }
      try {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, selectedWorkspaceId);
      } catch {
        /* private mode */
      }
      syncUrlFromAppState();
      loadSample().catch(showLoadError);
    });
    if (localExamplesToggle) {
      localExamplesToggle.addEventListener("click", () => { toggleLocalExamplesSources(); });
    }
  } else if (workspaceSelectWrap) {
    workspaceSelectWrap.hidden = true;
  }
  await loadSample();
}

function showLoadError(error) {
  errorBox.hidden = false;
  errorBox.textContent = error.message;
  buildTree(doc);
}

async function reloadDoc() {
  const button = document.querySelector("#reload");
  const previous = button.textContent;
  button.classList.add("is-busy");
  button.textContent = "Обновление…";
  try {
    if (docFileHandle) {
      await loadDocFromFile(await docFileHandle.getFile());
    } else if (docSource === "api" || docSource === "example") {
      await loadSample();
    } else {
      throw new Error("Повторная загрузка недоступна. Откройте JSON через «Открыть JSON».");
    }
    button.textContent = "Обновлено";
    setTimeout(() => { button.textContent = previous; }, 1200);
  } catch (error) {
    button.textContent = previous;
    errorBox.hidden = false;
    errorBox.textContent = error.message;
  } finally {
    button.classList.remove("is-busy");
  }
}

async function loadDocFromFile(file) {
  ingestDocument(JSON.parse(await file.text()));
  docFileName = file.name;
  docSource = "file";
  finishDocumentLoad();
}

function applyBootUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode");
  if (VALID_VIEW_MODES.has(mode)) viewMode = mode;
  const card = params.get("card");
  if (card) componentPreviewSurface = card;
  const page = params.get("page");
  if (page && pageSelect) pageSelect.value = page;
  const theme = params.get("theme");
  if (theme && THEME_VALUES.has(theme)) {
    setTheme(theme);
  }
  const viewportValue = params.get("viewport");
  if (viewportValue && viewport?.querySelector(`option[value="${CSS.escape(viewportValue)}"]`)) {
    viewport.value = viewportValue;
  }
  const workspace = params.get("workspace");
  if (workspace && isValidSourceId(workspace)) {
    selectedWorkspaceId = workspace;
    if (workspaceSelect?.querySelector(`option[value="${CSS.escape(workspace)}"]`)) {
      workspaceSelect.value = workspace;
    }
  }
}

function isFramePreviewKey(key, document) {
  const parsed = parseChromeKey(key);
  if (parsed?.part !== "frame") return false;
  const entries = listFrameWidgets(document);
  if (!parsed.frameRef) return entries.length > 0;
  return entries.some((entry) => entry.ref === parsed.frameRef);
}

function applySelectionFromUrlParams() {
  if (!doc) return;
  const params = new URLSearchParams(window.location.search);
  const keyParam = params.get("key");
  if (keyParam) {
    const decoded = decodeURIComponent(keyParam);
    if (resolveNodeByKey(decoded, doc) != null || isFramePreviewKey(decoded, doc)) {
      selectedKey = decoded;
      syncCardFromStateKey(selectedKey);
      syncCardFromChromeKey(selectedKey);
    } else {
      selectedKey = defaultStateKey();
    }
  } else {
    selectedKey = defaultStateKey();
  }
  const widgetName = parseWidgetKey(selectedKey);
  const preview = params.get("widgetPreview");
  if (widgetName && preview) {
    setWidgetPreviewMode(widgetName, preview);
  }
  const datasetId = params.get("widgetDataset");
  if (widgetName && datasetId) {
    setWidgetDatasetKind(widgetName, datasetId);
  }
  previewScopeKey = selectedKey;
}

function finishDocumentLoad() {
  applySelectionFromUrlParams();
  normalizeSelectionKeyForMode(viewMode);
  applyViewModeUi(viewMode);
  if (viewMode === "component") {
    setTreePaneCollapsed(false);
  }
  errorBox.hidden = true;
  resetTreeDefaults();
  expandTreeToKey(selectedKey);
  paint();
  updateNodeSaveButton();
  syncUrlFromAppState();
}

function syncUrlFromAppState() {
  if (!doc) return;
  clearTimeout(urlSyncTimer);
  urlSyncTimer = setTimeout(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("mode", viewMode);
    url.searchParams.set("card", activeSurface());
    url.searchParams.set("key", selectedKey);
    url.searchParams.set("theme", getTheme());
    url.searchParams.set("viewport", viewport.value);
    const locale = previewLocaleSelect?.value || DEFAULT_PREVIEW_LOCALE;
    if (isPreviewLocale(locale) && locale !== DEFAULT_PREVIEW_LOCALE) {
      url.searchParams.set("lang", locale);
    } else {
      url.searchParams.delete("lang");
    }
    url.searchParams.delete("contractApi");
    if (USE_ADMIN_CONTRACT) {
      url.searchParams.set("workspace", selectedWorkspaceId);
    } else {
      url.searchParams.delete("workspace");
    }
    if (viewMode === "page" && pageSelect?.value) {
      url.searchParams.set("page", pageSelect.value);
    } else {
      url.searchParams.delete("page");
    }
    const widgetName = parseWidgetKey(selectedKey);
    if (widgetName && viewMode === "component") {
      url.searchParams.set("widgetPreview", getWidgetPreviewMode(widgetName, doc));
      const datasetId = getWidgetDatasetRecordId(widgetName, doc);
      if (datasetId) url.searchParams.set("widgetDataset", datasetId);
      else url.searchParams.delete("widgetDataset");
    } else {
      url.searchParams.delete("widgetPreview");
      url.searchParams.delete("widgetDataset");
    }
    history.replaceState(null, "", url);
  }, 80);
}

function setComponentPreviewSurface(surface) {
  if (!surface || typeof surface !== "string") return;
  if (doc && !isValidComponentCard(surface)) return;
  componentPreviewSurface = surface;
}

function ensureComponentPreviewSurfaceValid() {
  if (componentPreviewSurface && doc && !isValidComponentCard(componentPreviewSurface)) {
    componentPreviewSurface = "";
  }
}

function activeSurface() {
  const fromChrome = resolveChromePreviewPart(null);
  if (fromChrome && (!doc || isValidComponentCard(fromChrome))) return fromChrome;
  const stateParsed = parseStateKey(selectedKey);
  if (stateParsed?.surface && (!doc || isValidComponentCard(stateParsed.surface))) {
    return stateParsed.surface;
  }
  if (componentPreviewSurface && (!doc || isValidComponentCard(componentPreviewSurface))) {
    return componentPreviewSurface;
  }
  const refs = doc ? listChromeFrameRefs(doc) : [];
  return refs[0] ?? listPageKeys(doc ?? {})[0] ?? "";
}

function activePageKey() {
  if (pageSelect?.value) return pageSelect.value;
  const keys = doc ? listPageKeys(doc) : [];
  return keys[0] ?? "";
}

function pageTreeSurfaces() {
  if (!doc) return [];
  return [...listChromeFrameRefs(doc), ...listPageKeys(doc)];
}

function surfacesWithLocalElements() {
  return [...CHROME_SURFACES_WITH_ELEMENTS, ...listPageKeys(doc ?? {})];
}

function isValidComponentCard(value) {
  if (!value || !doc) return false;
  return listComponentCardOptions(doc).some((row) => row.value === value);
}

function syncPageSelectOptions() {
  if (!pageSelect || !doc) return;
  const current = pageSelect.value;
  const keys = listPageKeys(doc);
  pageSelect.replaceChildren();
  for (const key of keys) {
    const option = document.createElement("option");
    option.value = key;
    option.textContent = key;
    pageSelect.append(option);
  }
  const keySet = new Set(keys);
  pageSelect.value = keySet.has(current) ? current : (keys[0] ?? "");
}

function getStateRoot(document) {
  return getStateRootForSurface(document, activeSurface());
}

function getStateRootForSurface(document, surface) {
  const card = surface ? getSurfaceCard(document, surface) : getSurfaceCard(document, activeSurface());
  return getViewFromCard(card);
}

function defaultStateKey() {
  if (viewMode === "page") {
    return `${activePageKey()}:state`;
  }
  const surface = activeSurface();
  if (surface === "header" || surface === "sidebar" || surface === "footer") {
    return "state";
  }
  return `${surface}:state`;
}

function isRootStateKey(key) {
  const parsed = parseStateKey(key);
  return Boolean(parsed && parsed.indices.length === 0);
}

function shouldRestoreComponentPreview(key) {
  if (viewMode !== "component" || !isStateNodeKey(key)) return false;
  if (isRootStateKey(key)) return true;
  return !preview.querySelector(":scope > .hv-root");
}

function stateKeyFromParsed(parsed, indexCount) {
  const indices = parsed.indices.slice(0, indexCount);
  if (parsed.surface) {
    return indices.length ? `${parsed.surface}:state.${indices.join(".")}` : `${parsed.surface}:state`;
  }
  return indices.length ? `state.${indices.join(".")}` : "state";
}

/** Map canvas path to nearest node that exists in the JSON tree (e.g. preview-expanded cards). */
function nearestStateKey(key, document) {
  const parsed = parseStateKey(key);
  if (!parsed) return key;
  for (let len = parsed.indices.length; len >= 0; len -= 1) {
    const candidate = stateKeyFromParsed(parsed, len);
    if (resolveNodeByKey(candidate, document) != null) return candidate;
  }
  return stateKeyFromParsed(parsed, 0);
}

function syncCardFromStateKey(key) {
  if (viewMode !== "component") return;
  const parsed = parseStateKey(key);
  if (!parsed?.surface) return;
  setComponentPreviewSurface(parsed.surface);
}

function syncCardFromChromeKey(key) {
  if (viewMode !== "component") return;
  const parsed = parseChromeKey(key);
  if (parsed?.pageKey && isValidComponentCard(parsed.pageKey)) {
    setComponentPreviewSurface(parsed.pageKey);
    return;
  }
  if (!parsed?.part || parsed.part === "pages") return;
  if (!isValidComponentCard(parsed.part)) return;
  setComponentPreviewSurface(parsed.part);
}

/** Component preview surface: chrome tree selection overrides card when part maps to a card. */
function resolveChromePreviewPart(fallbackSurface) {
  const parsed = parseChromeKey(selectedKey);
  if (!parsed?.part || parsed.part === "pages") return fallbackSurface;
  if (isValidComponentCard(parsed.part)) return parsed.part;
  return fallbackSurface;
}

function resolveComponentPreviewPageKey(selectedKey, doc) {
  if (!doc || !selectedKey) return null;
  const chromeParsed = parseChromeKey(selectedKey);
  if (chromeParsed?.pageKey) return chromeParsed.pageKey;
  if (chromeParsed?.part === "pages") return activePageKey();
  const stateParsed = parseStateKey(selectedKey);
  if (stateParsed?.surface && listPageKeys(doc).includes(stateParsed.surface)) {
    return stateParsed.surface;
  }
  const elementParsed = parseElementKey(selectedKey);
  if (
    elementParsed?.scope
    && elementParsed.scope !== "global"
    && listPageKeys(doc).includes(elementParsed.scope)
  ) {
    return elementParsed.scope;
  }
  return null;
}

function listChromeTreeParts(_document) {
  return ["frame", "pages"];
}

function normalizeSelectionKeyForMode(mode = viewMode) {
  if (mode === "page") {
    const parsed = parseStateKey(selectedKey);
    if (parsed && !parsed.surface) {
      selectedKey = `${activeSurface()}:${selectedKey}`;
    }
    previewScopeKey = selectedKey;
    return;
  }
  const parsed = parseStateKey(selectedKey);
  if (parsed?.surface) {
    if (parsed.surface === activeSurface()) {
      selectedKey = parsed.indices.length
        ? `state.${parsed.indices.join(".")}`
        : "state";
    } else {
      selectedKey = "state";
    }
  }
  previewScopeKey = selectedKey;
}

function isDefaultExpandedTreeKey(key) {
  if (key === "section:widgets" || key === "section:elements") return true;
  if (viewMode === "component") {
    return key === "section:chrome" || key === "section:chrome:pages" || key === "section:chrome:frame";
  }
  return key === "section:states"
    || key.startsWith("section:states:")
    || key.startsWith("section:page:");
}

function isTreeSectionKey(key) {
  return key === "section:states"
    || key?.startsWith("section:states:")
    || key === "section:chrome"
    || key?.startsWith("section:chrome:")
    || key === "section:elements"
    || key?.startsWith("section:elements:")
    || key?.startsWith("section:page:")
    || key?.startsWith("section:page-templates:")
    || key === "section:widgets";
}

function pageTreeSectionKey(pageKey) {
  return `section:page:${pageKey}`;
}

/** Canonical widget spec on doc.widgets (same object for read and write). */
function ensureWidgetSpec(document, name) {
  if (!name) return null;
  const catalog = getWidgets(document);
  const fromCatalog = catalog[name];
  if (!fromCatalog) return null;
  if (!document.widgets || typeof document.widgets !== "object") {
    document.widgets = { ...catalog };
  } else if (!document.widgets[name]) {
    document.widgets[name] = fromCatalog;
  }
  return document.widgets[name];
}

function getWidgetGroupScreenId() {
  return null;
}

/** Live template root: widget `view`. */
function getWidgetVisualRoot(spec) {
  if (!spec || typeof spec !== "object") return null;
  return spec.view ?? null;
}

function resolveWidgetNode(spec, parsed) {
  if (!spec || !parsed) return null;
  if (!parsed.section) return spec;
  if (parsed.section === "visual") {
    return walkVisual(spec, parsed.indices, previewPhaseId(parsed.name)).node;
  }
  if (parsed.section === "elements") {
    if (!parsed.elementName) return spec.elements ?? null;
    let node = spec.elements?.[parsed.elementName];
    for (const index of parsed.indices) {
      node = node?.items?.[index];
    }
    return node ?? null;
  }
  return null;
}

function nearestWidgetVisualKey(key, document) {
  const parsed = parseWidgetTreeKey(key);
  if (!parsed?.name || parsed.section !== "visual") return key;
  for (let len = parsed.indices.length; len >= 0; len -= 1) {
    const candidate = widgetVisualTreeKey(parsed.name, parsed.indices.slice(0, len));
    if (resolveNodeByKey(candidate, document) != null) return candidate;
  }
  return widgetStorageKey(parsed.name);
}

function nearestWidgetElementKey(key, document) {
  const parsed = parseWidgetTreeKey(key);
  if (!parsed?.name || parsed.section !== "elements" || !parsed.elementName) return key;
  for (let len = parsed.indices.length; len >= 0; len -= 1) {
    const candidate = widgetElementTreeKey(
      parsed.name,
      parsed.elementName,
      parsed.indices.slice(0, len),
    );
    if (resolveNodeByKey(candidate, document) != null) return candidate;
  }
  return widgetElementTreeKey(parsed.name, parsed.elementName);
}

function nearestElementKey(key, document) {
  const parsed = parseElementKey(key);
  if (!parsed?.name) return key;
  for (let len = parsed.indices.length; len >= 0; len -= 1) {
    const candidate = elementTreeKey(parsed.scope, parsed.name, parsed.indices.slice(0, len));
    if (resolveNodeByKey(candidate, document) != null) return candidate;
  }
  return elementStorageKey(parsed.scope, parsed.name);
}

function resolveNodeByKey(key, document) {
  if (!key) return null;
  if (key === "dataset") return getDataset(document);
  if (key.startsWith("dataset:")) {
    const parts = key.slice("dataset:".length).split("/").filter(Boolean);
    let cursor = getDataset(document);
    for (const part of parts) {
      if (!cursor || typeof cursor !== "object") return null;
      cursor = cursor[part];
    }
    return cursor ?? null;
  }
  if (key.startsWith("widget:")) {
    const parsed = parseWidgetTreeKey(key);
    if (!parsed?.name) return null;
    const spec = ensureWidgetSpec(document, parsed.name) ?? getWidgets(document)[parsed.name];
    if (!spec) return null;
    if (!parsed.section) return spec;
    return resolveWidgetNode(spec, parsed);
  }
  if (key.startsWith("element:")) {
    const parsed = parseElementKey(key);
    if (!parsed) return null;
    let node;
    if (parsed.scope === "global") {
      node = getGlobalElements(document)[parsed.name] ?? null;
    } else {
      node = getSurfaceCard(document, parsed.scope)?.elements?.[parsed.name] ?? null;
    }
    for (const index of parsed.indices) {
      node = node?.items?.[index];
      if (!node) return null;
    }
    return node;
  }
  if (key.startsWith("chrome:")) {
    const parsed = parseChromeKey(key);
    if (!parsed?.part) return null;
    const chrome = getChrome(document);
    if (parsed.pageKey) return chrome.pages?.[parsed.pageKey] ?? null;
    if (parsed.part === "pages") return chrome.pages ?? null;
    if (parsed.part === "frame") {
      if (!parsed.frameRef) return frameRootContract(document);
      return framePlacement(document, parsed.frameRef)?.node ?? null;
    }
    return chrome[parsed.part] ?? null;
  }
  const stateKey = parseStateKey(key);
  if (stateKey) {
    let node = getStateRootForSurface(document, stateKey.surface ?? activeSurface());
    for (const index of stateKey.indices) {
      node = node?.items?.[index];
      if (!node) return null;
    }
    return node;
  }
  return null;
}

function setNodeByKey(key, document, value) {
  if (!key) return false;
  if (key.startsWith("widget:")) {
    const parsed = parseWidgetTreeKey(key);
    if (!parsed?.name) return false;
    if (!document.chrome) document.chrome = { ...getChrome(document) };
    const spec = ensureWidgetSpec(document, parsed.name);
    if (!spec) return false;
    if (!parsed.section) {
      document.widgets[parsed.name] = value;
    } else if (parsed.section === "visual") {
      if (!parsed.indices.length) {
        spec.view = value;
      } else {
        const walked = walkVisual(spec, parsed.indices, previewPhaseId(parsed.name));
        if (!Array.isArray(walked.items) || walked.index == null) return false;
        walked.items[walked.index] = value;
      }
    } else if (parsed.section === "elements") {
      if (!parsed.elementName) {
        spec.elements = value;
      } else {
        if (!spec.elements) spec.elements = {};
        if (!parsed.indices.length) {
          spec.elements[parsed.elementName] = value;
        } else {
          let parent = spec.elements[parsed.elementName];
          for (let i = 0; i < parsed.indices.length - 1; i++) {
            parent = parent?.items?.[parsed.indices[i]];
            if (!parent) return false;
          }
          if (!parent?.items) return false;
          parent.items[parsed.indices.at(-1)] = value;
        }
      }
    } else {
      return false;
    }
    if (document.chrome?.widgets) {
      delete document.chrome.widgets[parsed.name];
      if (Object.keys(document.chrome.widgets).length === 0) {
        delete document.chrome.widgets;
      }
    }
    return true;
  }
  if (key.startsWith("chrome:")) {
    const parsed = parseChromeKey(key);
    if (!parsed?.part) return false;
    if (!document.chrome) document.chrome = { ...getChrome(document) };
    if (parsed.pageKey) {
      if (!document.chrome.pages) document.chrome.pages = { ...getPages(document) };
      document.chrome.pages[parsed.pageKey] = value;
      return true;
    }
    if (parsed.part === "pages") {
      document.chrome.pages = value;
      return true;
    }
    if (parsed.part === "frame") {
      if (!parsed.frameRef) {
        if (Array.isArray(document.chrome.frame)) {
          document.chrome.frame = value;
          return true;
        }
        if (!value || typeof value !== "object" || Array.isArray(value)) return false;
        for (const [slot, slotValue] of Object.entries(value)) {
          document.chrome[slot] = slotValue;
        }
        return true;
      }
      const placed = framePlacement(document, parsed.frameRef);
      if (!placed) return false;
      if (placed.path[0] === "frame") {
        document.chrome.frame[Number(placed.path[1])] = value;
        return true;
      }
      document.chrome[placed.path[0]] = value;
      return true;
    }
    document.chrome[parsed.part] = value;
    return true;
  }
  if (key.startsWith("element:")) {
    const parsed = parseElementKey(key);
    if (!parsed) return false;
    const chrome = getChrome(document);
    if (parsed.scope === "global") {
      if (!Array.isArray(document.elements)) {
        document.elements = Object.values(getGlobalElements(document));
      }
      const index = paletteIndexByName(document, parsed.name);
      if (index < 0) return false;
      if (!parsed.indices.length) {
        document.elements[index] = value;
      } else {
        let parent = document.elements[index];
        for (let i = 0; i < parsed.indices.length - 1; i++) {
          parent = parent?.items?.[parsed.indices[i]];
          if (!parent) return false;
        }
        if (!parent?.items) return false;
        parent.items[parsed.indices.at(-1)] = value;
      }
    } else {
      if (!document.chrome) document.chrome = { ...chrome };
      if (getPages(document)[parsed.scope]) {
        if (!document.chrome.pages) document.chrome.pages = { ...getPages(document) };
        const page = document.chrome.pages[parsed.scope];
        if (!page) return false;
        if (!page.elements) page.elements = {};
        if (!parsed.indices.length) {
          page.elements[parsed.name] = value;
        } else {
          let parent = page.elements[parsed.name];
          for (let i = 0; i < parsed.indices.length - 1; i++) {
            parent = parent?.items?.[parsed.indices[i]];
            if (!parent) return false;
          }
          if (!parent?.items) return false;
          parent.items[parsed.indices.at(-1)] = value;
        }
      } else {
        const card = document.chrome[parsed.scope];
        if (!card) return false;
        if (!card.elements) card.elements = {};
        if (!parsed.indices.length) {
          card.elements[parsed.name] = value;
        } else {
          let parent = card.elements[parsed.name];
          for (let i = 0; i < parsed.indices.length - 1; i++) {
            parent = parent?.items?.[parsed.indices[i]];
            if (!parent) return false;
          }
          if (!parent?.items) return false;
          parent.items[parsed.indices.at(-1)] = value;
        }
      }
    }
    return true;
  }
  const stateKey = parseStateKey(key);
  if (!stateKey) return false;
  const surface = stateKey.surface ?? activeSurface();
  const pages = getPages(document);
  if (!stateKey.indices.length) {
    if (pages[surface]) {
      if (!document.chrome) document.chrome = { ...getChrome(document) };
      if (!document.chrome.pages) document.chrome.pages = { ...pages };
      document.chrome.pages[surface].view = value;
      return true;
    }
    const widgetSpec = ensureWidgetSpec(document, surface);
    if (widgetSpec) {
      widgetSpec.view = value;
      return true;
    }
    return false;
  }
  let parent = getStateRootForSurface(document, surface);
  for (let i = 0; i < stateKey.indices.length - 1; i++) {
    parent = parent?.items?.[stateKey.indices[i]];
    if (!parent) return false;
  }
  if (!parent?.items) return false;
  parent.items[stateKey.indices.at(-1)] = value;
  return true;
}

function editorEditingCurrentSelection() {
  return (
    document.activeElement === source
    && !editorProgrammatic
    && editorLoadedKey === selectedKey
  );
}

function formatEditorJson(value) {
  editorProgrammatic = true;
  source.value = value != null ? `${JSON.stringify(value, null, 4)}\n` : "";
  editorProgrammatic = false;
  editorLoadedKey = selectedKey;
  updateEditorSwatches();
}

function showSelectedInEditor() {
  if (!doc) return;
  if (!editorEditingCurrentSelection()) {
    const node = resolveNodeByKey(selectedKey, doc);
    formatEditorJson(node);
  }
  updateJsonPaneTitle();
  refreshEditorContractCheck();
}

function measureEditorText(text) {
  if (!measureEl) {
    measureEl = document.createElement("span");
    measureEl.className = "editor-measure";
    source.parentElement?.append(measureEl);
  }
  const style = getComputedStyle(source);
  measureEl.style.font = style.font;
  measureEl.style.letterSpacing = style.letterSpacing;
  measureEl.textContent = text;
  return measureEl.getBoundingClientRect().width;
}

function updateEditorSwatches() {
  if (!sourceSwatches) return;
  sourceSwatches.replaceChildren();
  if (!doc?.tokens) return;

  const ctx = buildTokenContext(doc.tokens, getTheme());
  const lines = source.value.split("\n");
  const layer = document.createElement("div");
  layer.className = "editor-swatches__layer";
  layer.style.transform = `translate(${-source.scrollLeft}px, ${-source.scrollTop}px)`;
  sourceSwatches.append(layer);

  lines.forEach((line, index) => {
    const re = /@\{([^}]+)\}/g;
    let match = re.exec(line);
    while (match) {
      const name = match[1];
      const value = resolveColor(`@{${name}}`, ctx);
      if (!value || !isColorValue(value)) {
        match = re.exec(line);
        continue;
      }
      const colorKeyIndex = line.indexOf('"color"');
      const anchor = colorKeyIndex >= 0 ? colorKeyIndex : match.index;
      const before = line.slice(0, anchor);
      const left = EDITOR_PADDING_LEFT + measureEditorText(before) - SWATCH_SIZE - SWATCH_BEFORE_TEXT_GAP;
      const top = EDITOR_PADDING_TOP + index * EDITOR_LINE_HEIGHT + (EDITOR_LINE_HEIGHT - SWATCH_SIZE) / 2;

      const swatch = document.createElement("span");
      swatch.className = "editor-swatch";
      swatch.style.top = `${top}px`;
      swatch.style.left = `${left}px`;
      swatch.style.backgroundColor = value;
      swatch.title = `${name}: ${value}`;
      layer.append(swatch);
      match = re.exec(line);
    }
  });
}

function updateJsonPaneTitle() {
  const title = document.querySelector(".pane--editor .pane__title");
  if (!title) return;
  if (!selectedKey || selectedKey === "state") {
    title.textContent = "JSON · state";
    return;
  }
  title.textContent = `JSON · ${selectedKey}`;
}

function getViewportWidth() {
  const value = viewport.value;
  if (value !== "fluid") return Number(value);
  return Math.round(frame.getBoundingClientRect().width);
}

function setViewportPixels(targetPx) {
  if (!viewport) return;
  const target = String(targetPx);
  const hasExact = Array.from(viewport.options).some((option) => option.value === target);
  if (hasExact) {
    viewport.value = target;
  } else {
    let closestValue = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const option of viewport.options) {
      if (option.value === "fluid") continue;
      const width = Number(option.value);
      if (!Number.isFinite(width)) continue;
      const distance = Math.abs(width - targetPx);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestValue = option.value;
      }
    }
    if (closestValue) viewport.value = closestValue;
  }
  applyViewport();
}

function componentPreviewSurfaceKey(surface) {
  return resolveChromePreviewPart(surface);
}

function componentPreviewVisibilityNode(docForPreview, plan, surface) {
  if (plan.kind === "widget-visual" || plan.kind === "widget-element" || plan.kind === "element") {
    return plan.node;
  }
  if (isStateNodeKey(selectedKey)) {
    return resolveNodeByKey(selectedKey, docForPreview) ?? plan.node;
  }
  if (plan.kind === "surface") {
    const surfaceKey = componentPreviewSurfaceKey(surface);
    return getViewFromCard(getSurfaceCard(docForPreview, surfaceKey)) ?? plan.node;
  }
  return plan.node;
}

function clearPreviewBreakpointHint() {
  const hintEl = getPreviewBreakpointHintEl();
  if (!hintEl) return;
  hintEl.hidden = true;
  hintEl.replaceChildren();
}

function updatePreviewBreakpointHint(docForPreview, plan, surface, breakpoints, viewportWidth, renderedCount) {
  const previewBreakpointHint = getPreviewBreakpointHintEl();
  if (!previewBreakpointHint) return;
  const visibilityNode = componentPreviewVisibilityNode(docForPreview, plan, surface);
  const hint = buildBreakpointVisibilityHint(visibilityNode, breakpoints, viewportWidth);
  if (!hint || hint.currentVisible) {
    clearPreviewBreakpointHint();
    return;
  }

  previewBreakpointHint.hidden = false;
  previewBreakpointHint.replaceChildren();

  const title = document.createElement("p");
  title.className = "preview-breakpoint-hint__title";
  const bpLabel = hint.currentBreakpoint ?? "текущем";
  if (hint.suggestions.length === 0) {
    title.textContent = `На breakpoint «${bpLabel}» элемент скрыт (when.visibility: gone) и не отображается ни на одном breakpoint из контракта.`;
  } else {
    title.textContent = `На breakpoint «${bpLabel}» элемент скрыт (when.visibility: gone). Выберите ширину, где он виден:`;
  }
  previewBreakpointHint.append(title);

  if (hint.suggestions.length === 0) return;

  const actions = document.createElement("div");
  actions.className = "preview-breakpoint-hint__actions";
  const label = document.createElement("span");
  label.className = "preview-breakpoint-hint__label";
  label.textContent = "Ширина превью";
  actions.append(label);

  for (const suggestion of hint.suggestions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "preview-breakpoint-hint__btn";
    button.textContent = `${suggestion.name} · ${suggestion.viewportPx}px`;
    button.addEventListener("click", () => setViewportPixels(suggestion.viewportPx));
    actions.append(button);
  }
  previewBreakpointHint.append(actions);
}

function applyViewModeUi(mode) {
  for (const button of modeButtons) {
    const active = button.dataset.mode === mode;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-selected", String(active));
  }
  const isTokens = mode === "tokens";
  if (workspaceRoot) {
    workspaceRoot.dataset.mode = mode;
  }
  if (componentWorkspace && !isTokens) {
    componentWorkspace.dataset.view = mode;
  }
  if (componentWorkspace) {
    componentWorkspace.hidden = false;
  }
  if (tokensWorkspace) {
    tokensWorkspace.hidden = false;
  }
  componentStage.hidden = mode !== "component";
  pageWorkspace.hidden = mode !== "page";
  if (pageSelectWrap) pageSelectWrap.hidden = mode !== "page";
  setTreePaneCollapsed(mode === "page" || isTokens);
}

function setViewMode(mode) {
  if (!VALID_VIEW_MODES.has(mode)) return;
  if (mode !== "tokens") normalizeSelectionKeyForMode(mode);
  viewMode = mode;
  applyViewModeUi(mode);
  paint();
  syncUrlFromAppState();
}

function paint(options = {}) {
  if (!doc) {
    buildTree(null);
    return;
  }
  try {
    if (viewMode === "tokens") {
      updateTokensPanel(doc);
    } else if (viewMode === "page") {
      paintPage();
    } else {
      paintComponent(options);
    }
  } catch (error) {
    errorBox.hidden = false;
    errorBox.textContent = `Preview: ${error.message}`;
  }

  if (viewMode === "tokens") {
    updateNodeSaveButton();
    return;
  }

  buildTree(doc);
  syncWidgetPreviewBar();
  syncWidgetBehaviorToggle();
  if (options.refreshEditor !== false) {
    showSelectedInEditor();
  } else {
    updateJsonPaneTitle();
    refreshEditorContractCheck();
  }
  if (isStateNodeKey(selectedKey)) highlight(selectedKey);
  else if (isWidgetVisualTreeKey(selectedKey) || isWidgetElementTreeKey(selectedKey) || isWidgetGroupTreeKey(selectedKey)) {
    highlight(selectedKey);
  }
  refreshPropertiesInspector();
  updateNodeSaveButton();
}

function hasTreeChildren(node) {
  return Array.isArray(node?.items) && node.items.length > 0;
}

/** Global / page catalog element — nested tree when the template has items. */
function appendTreeElementNode(wrap, storageKey, name, template, depth) {
  if (!template || typeof template !== "object") {
    wrap.append(treeRow({
      key: storageKey,
      collapseKey: storageKey,
      label: name,
      depth,
      hasChildren: false,
    }));
    return;
  }
  appendTreeNode(wrap, template, storageKey, name, depth);
}

/** Widget `elements.*` template — same nested tree as visual state. */
function appendTreeWidgetElementNode(wrap, widgetName, elementName, template, depth) {
  const key = widgetElementTreeKey(widgetName, elementName);
  appendTreeElementNode(wrap, key, elementName, template, depth);
}

function widgetCatalogHasTreeChildren(spec) {
  return Boolean(getWidgetVisualRoot(spec));
}

/** `states[0]` and `elements` of a widget catalog entry. */
function appendTreeWidgetCatalogChildren(branch, name, spec, depth, options = {}) {
  const visual = options.skipVisual ? null : getWidgetVisualRoot(spec);

  if (visual) {
    appendTreeNode(
      branch,
      visual,
      widgetVisualTreeKey(name),
      nodeTitle(visual),
      depth,
    );
  } else {
    appendTreeElementNode(branch, widgetStorageKey(name), "widget", null, depth);
  }
}

/** Widget catalog entry: visual state tree + named element templates. */
function appendTreeWidgetNode(wrap, name, spec, depth) {
  if (!widgetCatalogHasTreeChildren(spec)) {
    appendTreeElementNode(wrap, widgetStorageKey(name), name, null, depth);
    return;
  }

  const branchKey = widgetStorageKey(name);
  const branch = document.createElement("div");
  branch.className = "tree-branch";
  branch.hidden = treeCollapsed.has(branchKey);
  appendTreeWidgetCatalogChildren(branch, name, spec, depth + 1);

  wrap.append(
    createTreeRowHead({
      row: {
        key: widgetStorageKey(name),
        collapseKey: branchKey,
        label: name,
        depth,
        hasChildren: true,
      },
      branch,
    }),
    branch,
  );
}

function normalizeLegacyElements() {
}

function normalizeSelectedElementKey() {
}

function ensureChromeOnDocument(document) {
  if (document.chrome) return;
  document.chrome = {
    header: document.header,
    sidebar: document.sidebar,
    footer: document.footer,
  };
  delete document.header;
  delete document.sidebar;
  delete document.footer;
}

function ensureElementsOnDocument(document) {
  if (document.elements == null && document.chrome?.elements != null) {
    document.elements = document.chrome.elements;
    delete document.chrome.elements;
    return;
  }
  if (document.elements != null && document.chrome?.elements != null) {
    document.elements = { ...document.chrome.elements, ...document.elements };
    delete document.chrome.elements;
  }
}

function ensureWidgetsOnDocument(document) {
  if (document.widgets == null && document.chrome?.widgets) {
    document.widgets = document.chrome.widgets;
  }
  if (document.chrome?.widgets) {
    delete document.chrome.widgets;
  }
}

function ensureDatasetOnDocument(document) {
  if (document.dataset != null) {
    delete document.preview;
    return;
  }
  if (document.preview != null) {
    document.dataset = document.preview;
  }
  delete document.preview;
}

function ensurePagesOnDocument() {
}

function ingestDocument(nextDoc) {
  doc = canonicalizeSduiDocument(nextDoc);
  session.doc = doc;
  ensureChromeOnDocument(doc);
  ensureElementsOnDocument(doc);
  ensurePagesOnDocument(doc);
  ensureWidgetsOnDocument(doc);
  ensureDatasetOnDocument(doc);
  normalizeLegacyElements(doc);
  normalizeSelectedElementKey();
  ensureComponentPreviewSurfaceValid();
  syncPageSelectOptions();
}

/** Page row in Structure (elements + shared templates + state), same nesting as widgets. */
function appendPageSurfaceTreeSection(doc, pageKey) {
  const surfaceRoot = getStateRootForSurface(doc, pageKey);
  const surfaceElements = getSurfaceCard(doc, pageKey)?.elements ?? {};
  const elementNames = Object.keys(surfaceElements);
  const templateNames = pageReferencedGlobalTemplates(doc, pageKey);
  const stateKey = stateStorageKey(pageKey);
  const hasState = Boolean(surfaceRoot);
  if (!hasState && elementNames.length === 0 && templateNames.length === 0) return;

  const branchKey = pageTreeSectionKey(pageKey);
  const branch = document.createElement("div");
  branch.className = "tree-branch";
  branch.hidden = treeCollapsed.has(branchKey);

  if (elementNames.length > 0) {
    const folderKey = `section:elements:${pageKey}`;
    const elementsBranch = document.createElement("div");
    elementsBranch.className = "tree-branch";
    elementsBranch.hidden = treeCollapsed.has(folderKey);
    for (const name of elementNames) {
      appendTreeElementNode(
        elementsBranch,
        elementStorageKey(pageKey, name),
        name,
        surfaceElements[name],
        2,
      );
    }
    branch.append(
      createTreeRowHead({
        row: {
          key: null,
          collapseKey: folderKey,
          label: "elements",
          depth: 1,
          hasChildren: true,
        },
        branch: elementsBranch,
      }),
      elementsBranch,
    );
  }

  const globalElements = getGlobalElements(doc);
  if (templateNames.length > 0) {
    const folderKey = `section:page-templates:${pageKey}`;
    const templatesBranch = document.createElement("div");
    templatesBranch.className = "tree-branch";
    templatesBranch.hidden = treeCollapsed.has(folderKey);
    for (const name of templateNames) {
      appendTreeElementNode(
        templatesBranch,
        elementStorageKey("global", name),
        name,
        globalElements[name],
        2,
      );
    }
    branch.append(
      createTreeRowHead({
        row: {
          key: null,
          collapseKey: folderKey,
          label: "templates",
          depth: 1,
          hasChildren: true,
        },
        branch: templatesBranch,
      }),
      templatesBranch,
    );
  }

  if (hasState) {
    appendTreeNode(branch, surfaceRoot, stateKey, nodeTitle(surfaceRoot), 1);
  }

  tree.append(
    createTreeRowHead({
      row: {
        key: hasState ? stateKey : null,
        collapseKey: branchKey,
        label: pageKey,
        depth: 0,
        hasChildren: true,
      },
      branch,
    }),
    branch,
  );
}

function appendSurfaceElementsSection(document, surface) {
  if (CHROME_SURFACES_WITH_ELEMENTS.includes(surface)) return;
  const surfaceElements = getSurfaceCard(document, surface)?.elements;
  if (!surfaceElements || Object.keys(surfaceElements).length === 0) return;
  tree.append(buildTreeSection({
    title: `${surface} · elements`,
    sectionKey: `section:elements:${surface}`,
    selectKey: null,
    depth: 0,
    hasChildren: true,
    renderChildren(wrap) {
      for (const name of Object.keys(surfaceElements)) {
        appendTreeElementNode(
          wrap,
          elementStorageKey(surface, name),
          name,
          surfaceElements[name],
          1,
        );
      }
    },
  }));
}

function appendSurfaceStateSection(document, surface) {
  const surfaceRoot = getStateRootForSurface(document, surface);
  if (!surfaceRoot) return;
  const stateKey = stateStorageKey(surface);
  tree.append(buildTreeSection({
    title: `${surface} · state`,
    sectionKey: `section:states:${surface}`,
    selectKey: stateKey,
    depth: 0,
    hasChildren: hasTreeChildren(surfaceRoot),
    renderChildren(branch) {
      appendTreeNode(branch, surfaceRoot, stateKey, nodeTitle(surfaceRoot), 1);
    },
  }));
}

function appendChromePartStateChildren(branch, doc, part, depth) {
  if (part === "auth" || part === "pages") return;

  const widgetSpec = getWidgets(doc)?.[part];
  const shell = part === "header" || part === "sidebar" || part === "footer";
  if (!shell && widgetSpec && getWidgetVisualRoot(widgetSpec)) return;

  const surfaceRoot = getStateRootForSurface(doc, part);
  if (!surfaceRoot) return;

  if (shell) {
    if (part !== activeSurface()) return;
    appendTreeNode(branch, surfaceRoot, "state", nodeTitle(surfaceRoot), depth);
    return;
  }

  appendTreeNode(
    branch,
    surfaceRoot,
    stateStorageKey(part),
    nodeTitle(surfaceRoot),
    depth,
  );
}

function appendChromeTreePageNode(wrap, doc, pageKey, depth) {
  const pageKeyStorage = chromePageStorageKey(pageKey);
  const surfaceRoot = getStateRootForSurface(doc, pageKey);
  const surfaceElements = getSurfaceCard(doc, pageKey)?.elements ?? {};
  const elementNames = Object.keys(surfaceElements);
  const templateNames = pageReferencedGlobalTemplates(doc, pageKey);
  const branchKey = chromePageSectionKey(pageKey);
  const branch = document.createElement("div");
  branch.className = "tree-branch";
  branch.hidden = treeCollapsed.has(branchKey);

  if (elementNames.length > 0) {
    const folderKey = `section:elements:${pageKey}`;
    const elementsBranch = document.createElement("div");
    elementsBranch.className = "tree-branch";
    elementsBranch.hidden = treeCollapsed.has(folderKey);
    for (const name of elementNames) {
      appendTreeElementNode(
        elementsBranch,
        elementStorageKey(pageKey, name),
        name,
        surfaceElements[name],
        depth + 2,
      );
    }
    branch.append(
      createTreeRowHead({
        row: {
          key: null,
          collapseKey: folderKey,
          label: "elements",
          depth: depth + 1,
          hasChildren: true,
        },
        branch: elementsBranch,
      }),
      elementsBranch,
    );
  }

  const globalElementsForPage = getGlobalElements(doc);
  if (templateNames.length > 0) {
    const folderKey = `section:page-templates:${pageKey}`;
    const templatesBranch = document.createElement("div");
    templatesBranch.className = "tree-branch";
    templatesBranch.hidden = treeCollapsed.has(folderKey);
    for (const name of templateNames) {
      appendTreeElementNode(
        templatesBranch,
        elementStorageKey("global", name),
        name,
        globalElementsForPage[name],
        depth + 2,
      );
    }
    branch.append(
      createTreeRowHead({
        row: {
          key: null,
          collapseKey: folderKey,
          label: "templates",
          depth: depth + 1,
          hasChildren: true,
        },
        branch: templatesBranch,
      }),
      templatesBranch,
    );
  }

  if (surfaceRoot) {
    appendTreeNode(
      branch,
      surfaceRoot,
      stateStorageKey(pageKey),
      nodeTitle(surfaceRoot),
      depth + 1,
    );
  }

  const hasChildren = branch.childElementCount > 0;
  wrap.append(
    createTreeRowHead({
      row: {
        key: pageKeyStorage,
        collapseKey: branchKey,
        label: pageKey,
        depth,
        hasChildren: true,
      },
      branch,
    }),
    branch,
  );
}

function appendChromeTreePart(wrap, doc, part, depth) {
  if (part === "frame") {
    const entries = listFrameWidgets(doc);
    const branchKey = chromePartSectionKey("frame");
    const branch = document.createElement("div");
    branch.className = "tree-branch";
    branch.hidden = treeCollapsed.has(branchKey);
    for (const entry of entries) {
      branch.append(treeRow({
        key: chromeFrameWidgetKey(entry.ref),
        collapseKey: chromeFrameWidgetKey(entry.ref),
        label: entry.ref,
        depth: depth + 1,
        hasChildren: false,
      }));
    }
    wrap.append(
      createTreeRowHead({
        row: {
          key: chromeStorageKey("frame"),
          collapseKey: branchKey,
          label: "frame",
          depth,
          hasChildren: entries.length > 0,
        },
        branch: entries.length > 0 ? branch : undefined,
      }),
    );
    if (entries.length > 0) wrap.append(branch);
    return;
  }
  if (part === "pages") {
    const pageKeys = listPageKeys(doc);
    const branchKey = chromePartSectionKey("pages");
    const branch = document.createElement("div");
    branch.className = "tree-branch";
    branch.hidden = treeCollapsed.has(branchKey);
    for (const pageKey of pageKeys) {
      appendChromeTreePageNode(branch, doc, pageKey, depth + 1);
    }
    wrap.append(
      createTreeRowHead({
        row: {
          key: chromeStorageKey("pages"),
          collapseKey: branchKey,
          label: "pages",
          depth,
          hasChildren: pageKeys.length > 0,
        },
        branch,
      }),
      branch,
    );
    return;
  }

  const branchKey = chromePartSectionKey(part);
  const branch = document.createElement("div");
  branch.className = "tree-branch";
  branch.hidden = treeCollapsed.has(branchKey);

  const widgetSpec = getWidgets(doc)?.[part];
  if (widgetSpec && typeof widgetSpec === "object") {
    const shell = part === "header" || part === "sidebar" || part === "footer";
    appendTreeWidgetCatalogChildren(branch, part, widgetSpec, depth + 1, { skipVisual: shell });
  }
  appendChromePartStateChildren(branch, doc, part, depth + 1);

  const hasChildren = branch.childElementCount > 0;
  wrap.append(
    createTreeRowHead({
      row: {
        key: chromeStorageKey(part),
        collapseKey: branchKey,
        label: part,
        depth,
        hasChildren,
      },
      branch: hasChildren ? branch : undefined,
    }),
  );
  if (hasChildren) wrap.append(branch);
}

function appendChromeTreeSection(doc) {
  tree.append(buildTreeSection({
    title: "chrome",
    sectionKey: "section:chrome",
    selectKey: null,
    depth: 0,
    hasChildren: true,
    renderChildren(wrap) {
      for (const part of listChromeTreeParts(doc)) {
        appendChromeTreePart(wrap, doc, part, 1);
      }
    },
  }));
}

function buildTreeBody(doc) {
  if (selectedKey === "dataset" || selectedKey?.startsWith("dataset:")) {
    selectedKey = defaultStateKey();
    previewScopeKey = selectedKey;
  }

  const widgets = doc.widgets;
  const widgetNames = listWidgetCatalogNames(doc);
  if (widgets && typeof widgets === "object" && widgetNames.length > 0) {
    tree.append(buildTreeSection({
      title: "widgets",
      sectionKey: "section:widgets",
      selectKey: null,
      depth: 0,
      hasChildren: true,
      renderChildren(wrap) {
        for (const name of widgetNames) {
          appendTreeWidgetNode(wrap, name, widgets[name], 1);
        }
      },
    }));
  }

  const globalElementNames = globalElementNamesForTree(doc);
  if (globalElementNames.length > 0) {
    tree.append(buildTreeSection({
      title: "elements",
      sectionKey: "section:elements",
      selectKey: null,
      depth: 0,
      hasChildren: true,
      renderChildren(wrap) {
        const globalElements = getGlobalElements(doc);
        for (const name of globalElementNames) {
          appendTreeElementNode(
            wrap,
            elementStorageKey("global", name),
            name,
            globalElements[name],
            1,
          );
        }
      },
    }));
  }

  if (viewMode === "page") {
    const pageKeys = new Set(listPageKeys(doc));
    for (const surface of pageTreeSurfaces()) {
      if (CHROME_SURFACES_WITH_ELEMENTS.includes(surface)) {
        appendSurfaceStateSection(doc, surface);
      } else if (pageKeys.has(surface)) {
        appendPageSurfaceTreeSection(doc, surface);
      } else {
        appendSurfaceStateSection(doc, surface);
      }
    }
  }

  if (viewMode === "component") {
    appendChromeTreeSection(doc);
  }
}

function resetTreeDefaults() {
  treeDefaultsApplied = false;
  treeCollapsed.clear();
}

function applyDefaultTreeCollapse() {
  if (treeDefaultsApplied) return;
  treeDefaultsApplied = true;
  for (const row of tree.querySelectorAll(".tree-leaf[data-collapse-key]")) {
    const key = row.dataset.collapseKey;
    if (isDefaultExpandedTreeKey(key)) continue;
    treeCollapsed.add(key);
  }
  syncTreeCollapseDom();
}

function buildTreeSection({ title, sectionKey, selectKey, depth, hasChildren, renderChildren }) {
  const wrap = document.createElement("div");
  wrap.className = "tree-section";

  if (hasChildren) {
    const branch = document.createElement("div");
    branch.className = "tree-branch";
    branch.hidden = treeCollapsed.has(sectionKey);
    renderChildren(branch);
    wrap.append(
      createTreeRowHead({
        row: {
          key: selectKey,
          collapseKey: sectionKey,
          label: title,
          depth,
          hasChildren: true,
        },
        branch,
      }),
      branch,
    );
  } else {
    wrap.append(treeRow({
      key: selectKey,
      collapseKey: sectionKey,
      label: title,
      depth,
      hasChildren: false,
    }));
  }
  return wrap;
}

function treeNodeChildren(node, key) {
  const parsed = parseWidgetTreeKey(key);
  if (parsed?.name && parsed.section === "visual") {
    const spec = getWidgets(doc)?.[parsed.name];
    return visualChildren(node, spec, previewPhaseId(parsed.name));
  }
  return Array.isArray(node?.items) ? node.items : [];
}

function appendTreeChildren(wrap, node, key, depth) {
  for (const [index, child] of treeNodeChildren(node, key).entries()) {
    appendTreeNode(wrap, child, `${key}.${index}`, nodeTitle(child), depth);
  }
}

function appendTreeNode(wrap, node, key, label, depth) {
  const branchKey = key;
  const hasChildren = treeNodeChildren(node, key).length > 0;
  if (!hasChildren) {
    wrap.append(treeRow({ key, collapseKey: branchKey, label, depth, hasChildren: false }));
    return;
  }
  const branch = document.createElement("div");
  branch.className = "tree-branch";
  branch.hidden = treeCollapsed.has(branchKey);
  appendTreeChildren(branch, node, key, depth + 1);
  wrap.append(
    createTreeRowHead({
      row: { key, collapseKey: branchKey, label, depth, hasChildren: true },
      branch,
    }),
    branch,
  );
}

function createTreeRowHead({ row, branch }) {
  const head = document.createElement("div");
  head.className = "tree-row-head";
  head.append(treeRow(row));
  head.append(createTreeBulkActions(() => branch, row.collapseKey));
  return head;
}

function treeTooltip() {
  if (!treeTooltipEl) {
    treeTooltipEl = document.createElement("div");
    treeTooltipEl.className = "tree-tooltip";
    treeTooltipEl.hidden = true;
    document.body.append(treeTooltipEl);
  }
  return treeTooltipEl;
}

function hideTreeTooltip() {
  clearTimeout(treeTooltipTimer);
  if (treeTooltipEl) treeTooltipEl.hidden = true;
}

function attachTreeLabelTooltip(label, text) {
  label.addEventListener("mouseenter", () => {
    hideTreeTooltip();
    if (label.scrollWidth <= label.clientWidth) return;
    treeTooltipTimer = setTimeout(() => {
      const tip = treeTooltip();
      tip.textContent = text;
      tip.hidden = false;
      const rect = label.getBoundingClientRect();
      tip.style.left = `${rect.left}px`;
      tip.style.top = `${rect.bottom + 6}px`;
      tip.style.maxWidth = `${Math.max(rect.width, 320)}px`;
    }, TREE_TOOLTIP_DELAY_MS);
  });
  label.addEventListener("mouseleave", hideTreeTooltip);
}

function findBranchForRow(rowButton) {
  const head = rowButton.closest(".tree-row-head");
  if (head) {
    const branch = head.nextElementSibling;
    return branch?.classList.contains("tree-branch") ? branch : null;
  }
  const next = rowButton.nextElementSibling;
  return next?.classList.contains("tree-branch") ? next : null;
}

function collectCollapseKeysFromBranch(branch) {
  if (!branch) return [];
  return [...branch.querySelectorAll(".tree-leaf[data-collapse-key]")].map((row) => row.dataset.collapseKey);
}

function syncTreeCollapseDom() {
  for (const row of tree.querySelectorAll(".tree-leaf[data-collapse-key]")) {
    const key = row.dataset.collapseKey;
    const collapsed = treeCollapsed.has(key);
    const branch = findBranchForRow(row);
    if (branch) branch.hidden = collapsed;
    row.querySelector(".tree-leaf__chevron")?.classList.toggle("is-collapsed", collapsed);
  }
}

function collectTreeAncestorKeys(key) {
  if (!key) return [];
  const keys = [];
  if (key.startsWith("widget:")) {
    const parsed = parseWidgetTreeKey(key);
    if (!parsed?.name) return keys;
    if (viewMode === "component" && doc && isChromeWidgetSurface(doc, parsed.name)) {
      keys.push("section:chrome");
      keys.push(chromePartSectionKey("frame"));
    } else {
      keys.push("section:widgets");
    }
    if (parsed.section) {
      keys.push(widgetStorageKey(parsed.name));
    }
    if (parsed.section === "visual") {
      let path = widgetVisualTreeKey(parsed.name);
      keys.push(path);
      for (const index of parsed.indices) {
        path += `.${index}`;
        keys.push(path);
      }
    } else if (parsed.section === "group") {
      keys.push(widgetGroupFolderKey(parsed.name));
      if (parsed.screenId) {
        let path = widgetGroupTreeKey(parsed.name, parsed.screenId);
        keys.push(path);
        for (const index of parsed.indices) {
          path += `.${index}`;
          keys.push(path);
        }
      }
    } else if (parsed.section === "elements") {
      keys.push(widgetElementsFolderKey(parsed.name));
      if (!parsed.elementName) return keys;
      let path = widgetElementTreeKey(parsed.name, parsed.elementName);
      keys.push(path);
      for (const index of parsed.indices) {
        path += `.${index}`;
        keys.push(path);
      }
    }
    return keys;
  }
  if (key.startsWith("element:")) {
    const parsed = parseElementKey(key);
    if (parsed?.scope === "global") keys.push("section:elements");
    else if (parsed?.scope) {
      if (doc && listPageKeys(doc).includes(parsed.scope)) {
        keys.push(pageTreeSectionKey(parsed.scope));
      }
      keys.push(`section:elements:${parsed.scope}`);
    }
    if (parsed?.name) {
      let path = elementStorageKey(parsed.scope, parsed.name);
      keys.push(path);
      for (const index of parsed.indices) {
        path += `.${index}`;
        keys.push(path);
      }
    }
    return keys;
  }
  if (key.startsWith("chrome:")) {
    keys.push("section:chrome");
    const parsed = parseChromeKey(key);
    if (parsed?.pageKey) {
      keys.push(chromePartSectionKey("pages"));
    }
    if (parsed?.part === "frame") keys.push(chromePartSectionKey("frame"));
    return keys;
  }
  const stateKey = parseStateKey(key);
  if (stateKey) {
    const base = stateStorageKey(stateKey.surface);
    if (viewMode === "component") {
      keys.push("section:chrome");
      const surface = stateKey.surface ?? activeSurface();
      if (doc && listPageKeys(doc).includes(surface)) {
        keys.push(chromePartSectionKey("pages"));
        keys.push(chromePageSectionKey(surface));
      } else if (surface.startsWith("auth-")) {
        keys.push(chromePartSectionKey("auth"));
      } else {
        keys.push(chromePartSectionKey(surface));
      }
    } else if (stateKey.surface) {
      if (doc && listPageKeys(doc).includes(stateKey.surface)) {
        keys.push(pageTreeSectionKey(stateKey.surface));
      } else {
        keys.push(`section:states:${stateKey.surface}`);
      }
    } else keys.push("section:states");
    keys.push(base);
    let path = base;
    for (const index of stateKey.indices) {
      path += `.${index}`;
      keys.push(path);
    }
    return keys;
  }
  return keys;
}

function expandTreeToKey(key) {
  if (!key) return;
  for (const ancestorKey of collectTreeAncestorKeys(key)) {
    treeCollapsed.delete(ancestorKey);
  }
  syncTreeCollapseDom();
}

function expandAllDescendants(branch, rowCollapseKey) {
  if (rowCollapseKey) treeCollapsed.delete(rowCollapseKey);
  for (const key of collectCollapseKeysFromBranch(branch)) {
    treeCollapsed.delete(key);
  }
  syncTreeCollapseDom();
}

function collapseAllDescendants(branch) {
  for (const key of collectCollapseKeysFromBranch(branch)) {
    treeCollapsed.add(key);
  }
  syncTreeCollapseDom();
}

function createTreeBulkActions(getBranch, rowCollapseKey) {
  const actions = document.createElement("div");
  actions.className = "tree-bulk-actions";

  const expandBtn = document.createElement("button");
  expandBtn.type = "button";
  expandBtn.className = "tree-bulk-btn";
  expandBtn.setAttribute("aria-label", "Раскрыть все");
  expandBtn.title = "Раскрыть все";
  expandBtn.append(createTreeBulkIcon("expand"));
  expandBtn.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    expandAllDescendants(getBranch(), rowCollapseKey);
  });

  const collapseBtn = document.createElement("button");
  collapseBtn.type = "button";
  collapseBtn.className = "tree-bulk-btn";
  collapseBtn.setAttribute("aria-label", "Свернуть все");
  collapseBtn.title = "Свернуть все";
  collapseBtn.append(createTreeBulkIcon("collapse"));
  collapseBtn.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    collapseAllDescendants(getBranch());
  });

  actions.append(expandBtn, collapseBtn);
  return actions;
}

function createTreeBulkIcon(kind) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("tree-bulk-btn__icon");

  const paths = kind === "expand"
    ? [
      "M2.697 1.53a.9.9 0 0 1 1.273 0L8 5.56l4.03-4.03a.9.9 0 1 1 1.273 1.273L8.637 7.47a.9.9 0 0 1-1.273 0L2.697 2.803a.9.9 0 0 1 0-1.273z",
      "M2.697 6.53a.9.9 0 0 1 1.273 0L8 10.56l4.03-4.03a.9.9 0 1 1 1.273 1.273L8.637 12.47a.9.9 0 0 1-1.273 0L2.697 7.803a.9.9 0 0 1 0-1.273z",
    ]
    : [
      "M2.697 9.47a.9.9 0 0 0 1.273 0L8 5.44l4.03 4.03a.9.9 0 1 0 1.273-1.273L8.637 3.53a.9.9 0 0 0-1.273 0L2.697 8.197a.9.9 0 0 0 0 1.273z",
      "M2.697 4.47a.9.9 0 0 0 1.273 0L8 0.44l4.03 4.03a.9.9 0 1 0 1.273-1.273L8.637 0.53a.9.9 0 0 0-1.273 0L2.697 3.197a.9.9 0 0 0 0 1.273z",
    ];

  for (const d of paths) {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("fill", "currentColor");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

function createTreeChevron(collapsed) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("aria-hidden", "true");
  svg.classList.add("tree-leaf__chevron");
  if (collapsed) svg.classList.add("is-collapsed");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("fill-rule", "evenodd");
  path.setAttribute("clip-rule", "evenodd");
  path.setAttribute("d", TREE_CHEVRON_PATH);
  svg.append(path);
  return svg;
}

function treeRow(row) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "tree-leaf";
  button.style.paddingLeft = `${8 + row.depth * 14}px`;

  const mark = document.createElement("span");
  mark.className = "tree-leaf__mark";
  if (row.hasChildren) {
    button.dataset.collapseKey = row.collapseKey;
    mark.classList.add("tree-leaf__toggle");
    mark.append(createTreeChevron(treeCollapsed.has(row.collapseKey)));
    mark.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const collapsed = treeCollapsed.has(row.collapseKey);
      if (collapsed) treeCollapsed.delete(row.collapseKey);
      else treeCollapsed.add(row.collapseKey);
      const rowButton = mark.closest(".tree-leaf");
      const branch = findBranchForRow(rowButton);
      if (branch) branch.hidden = !collapsed;
      const chevron = mark.querySelector(".tree-leaf__chevron");
      chevron?.classList.toggle("is-collapsed", !collapsed);
    });
  } else {
    mark.textContent = row.depth ? "·" : "▾";
  }
  button.append(mark);

  const label = document.createElement("span");
  label.className = "tree-leaf__label";
  label.textContent = row.label;
  attachTreeLabelTooltip(label, row.label);
  button.append(label);

  if (row.key === selectedKey) {
    button.classList.add("is-selected");
  }
  button.addEventListener("click", () => {
    if (!row.key) return;
    selectedKey = row.key;
    expandTreeToKey(selectedKey);
    syncCardFromChromeKey(selectedKey);
    if (isStateNodeKey(selectedKey)) {
      if (shouldRestoreComponentPreview(selectedKey)) {
        previewScopeKey = selectedKey;
        paint();
        syncUrlFromAppState();
        return;
      }
      highlight(selectedKey);
      markTree();
      showSelectedInEditor();
      scrollTreeToSelected();
      syncUrlFromAppState();
      return;
    }
    previewScopeKey = selectedKey;
    if (isWidgetVisualTreeKey(selectedKey) || isWidgetElementTreeKey(selectedKey) || isWidgetGroupTreeKey(selectedKey)) {
      markTree();
      scrollTreeToSelected();
      showSelectedInEditor();
      paint();
      syncUrlFromAppState();
      return;
    }
    const widgetFolder = parseWidgetTreeKey(selectedKey);
    if (widgetFolder?.section === "elements" && !widgetFolder.elementName) {
      markTree();
      scrollTreeToSelected();
      showSelectedInEditor();
      paint();
      syncUrlFromAppState();
      return;
    }
    if (isChromeTreeKey(selectedKey)) {
      markTree();
      showSelectedInEditor();
      scrollTreeToSelected();
      paint();
      syncUrlFromAppState();
      return;
    }
    markTree();
    scrollTreeToSelected();
    paint();
    syncUrlFromAppState();
  });
  if (row.key) button.dataset.key = row.key;
  return button;
}

function markTree() {
  for (const button of tree.querySelectorAll(".tree-leaf")) {
    button.classList.toggle("is-selected", button.dataset.key === selectedKey);
  }
}

function highlightScope() {
  return viewMode === "page" ? pageCanvas.row : preview;
}

function clearHoverHighlight() {
  highlightScope().querySelectorAll(".is-hover").forEach((el) => el.classList.remove("is-hover"));
}

function highlightHover(key) {
  clearHoverHighlight();
  if (!key || key.startsWith("element:")) return;
  if (key.startsWith("widget:")
    && !isWidgetVisualTreeKey(key)
    && !isWidgetElementTreeKey(key)
    && !isWidgetGroupTreeKey(key)) return;
  const scope = highlightScope();
  const matches = scope.querySelectorAll(`[data-hv-key="${CSS.escape(key)}"]`);
  for (const el of matches) el.classList.add("is-hover");
}

function highlight(key) {
  if (!key || key.startsWith("element:")) return;
  if (key.startsWith("widget:")
    && !isWidgetVisualTreeKey(key)
    && !isWidgetElementTreeKey(key)
    && !isWidgetGroupTreeKey(key)) return;
  const scope = highlightScope();
  scope.querySelectorAll(".is-hit").forEach((el) => el.classList.remove("is-hit"));
  const matches = scope.querySelectorAll(`[data-hv-key="${CSS.escape(key)}"]`);
  if (!matches.length) return;
  for (const el of matches) el.classList.add("is-hit");
  matches[0].scrollIntoView({ block: "nearest", inline: "nearest" });
}

function nearestWidgetGroupKey(key, document) {
  const parsed = parseWidgetTreeKey(key);
  if (!parsed?.name || parsed.section !== "group" || !parsed.screenId) return key;
  for (let len = parsed.indices.length; len >= 0; len -= 1) {
    const candidate = widgetGroupTreeKey(parsed.name, parsed.screenId, parsed.indices.slice(0, len));
    if (resolveNodeByKey(candidate, document) != null) return candidate;
  }
  return widgetGroupTreeKey(parsed.name, parsed.screenId);
}

function loadWidgetGroupActiveScreen() {
  try {
    const raw = localStorage.getItem(WIDGET_GROUP_SCREEN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function setWidgetGroupScreenId(widgetName, screenId) {
  if (!widgetName || !screenId) return;
  widgetGroupActiveScreen = { ...widgetGroupActiveScreen, [widgetName]: screenId };
  try {
    localStorage.setItem(WIDGET_GROUP_SCREEN_STORAGE_KEY, JSON.stringify(widgetGroupActiveScreen));
  } catch {
    /* ignore */
  }
}

function findWidgetContainerById(spec, id) {
  if (!spec || !id) return null;
  let found = null;
  const visit = (node) => {
    if (node?.id === id) found = node;
  };
  if (spec.view) walkWidgetSpecNodes(spec.view, visit);
  if (Array.isArray(spec.phases)) {
    for (const phase of spec.phases) {
      for (const item of phase.items ?? []) walkWidgetSpecNodes(item, visit);
    }
  }
  return found;
}

function resolveShowFromPaintedNode(el) {
  const hvId = el?.dataset?.hvId;
  if (hvId && rendered?.nodes) {
    const show = rendered.nodes.get(Number(hvId))?.node?.action?.show;
    if (typeof show === "string" && show) return show;
  }
  return null;
}

function tryHandleWidgetBehaviorClick(event) {
  if (!widgetBehaviorMode || !doc || !preview) return false;
  const widgetName = resolveActivePreviewWidgetName();
  if (!widgetName) return false;
  const spec = getWidgets(doc)[widgetName];
  if (!spec) return false;

  let el = event.target instanceof Element ? event.target.closest(".hv-node") : null;
  while (el && preview.contains(el)) {
    const showFromPaint = resolveShowFromPaintedNode(el);
    const key = el.dataset.hvKey;
    const nodeFromKey = key ? resolveNodeByKey(key, doc) : null;
    const showFromKey = nodeFromKey?.interaction?.show ?? nodeFromKey?.action?.show;
    const nodeFromId = el.id ? findWidgetContainerById(spec, el.id) : null;
    const showFromId = nodeFromId?.interaction?.show;
    const show = showFromPaint ?? showFromKey ?? showFromId;
    if (typeof show === "string" && show) {
      const vars = mergePageVars(doc, getTheme(), activePageKey());
      createPreviewOnAction(vars)({ show }, { widgetName, el });
      return true;
    }
    const parent = el.parentElement;
    el = parent ? parent.closest(".hv-node") : null;
  }
  return false;
}

function handlePreviewCanvasClick(event) {
  if (tryHandleWidgetBehaviorClick(event)) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  selectFromCanvas(event);
}

function selectFromCanvas(event) {
  // Canvas selection updates the tree only; the stage stays on previewScopeKey.
  const target = event.target.closest("[data-hv-key]");
  if (!target?.dataset.hvKey) return;
  if (event.target.closest("a")) event.preventDefault();
  const canvasKey = target.dataset.hvKey;
  let key = canvasKey;
  if (isStateNodeKey(canvasKey)) {
    key = nearestStateKey(canvasKey, doc);
    syncCardFromStateKey(key);
  } else if (canvasKey.includes("/group/")) {
    key = nearestWidgetGroupKey(canvasKey, doc);
  } else if (canvasKey.includes("/visual")) {
    key = nearestWidgetVisualKey(canvasKey, doc);
  } else if (canvasKey.includes("/elements/")) {
    key = nearestWidgetElementKey(canvasKey, doc);
  } else if (canvasKey.startsWith("element:")) {
    key = nearestElementKey(canvasKey, doc);
  }
  selectedKey = key;
  expandTreeToKey(selectedKey);
  if (selectedKey.startsWith("element:")) {
    markTree();
    scrollTreeToSelected();
    showSelectedInEditor();
    paint({ refreshEditor: false });
    syncUrlFromAppState();
    return;
  }
  if (isWidgetVisualTreeKey(selectedKey) || isWidgetElementTreeKey(selectedKey) || isWidgetGroupTreeKey(selectedKey)) {
    highlight(canvasKey);
    if (!highlightScope().querySelector(".is-hit")) highlight(selectedKey);
    markTree();
    scrollTreeToSelected();
    showSelectedInEditor();
    refreshPropertiesInspector();
    syncUrlFromAppState();
    return;
  }
  highlight(canvasKey);
  if (!highlightScope().querySelector(".is-hit")) highlight(key);
  markTree();
  scrollTreeToSelected();
  showSelectedInEditor();
  refreshPropertiesInspector();
  syncUrlFromAppState();
}

function scrollTreeToSelected() {
  tree.querySelector(".tree-leaf.is-selected")?.scrollIntoView({ block: "nearest" });
}

function previewPhaseByWidget() {
  if (!doc) return {};
  const out = {};
  for (const name of listWidgetCatalogNames(doc)) {
    out[name] = getWidgetPreviewMode(name, doc);
  }
  return out;
}

function previewSlotOptions() {
  return {};
}

function createPreviewOnAction(vars) {
  return (action, meta) => {
    const show = action?.show;
    if (typeof show === "string" && show && widgetBehaviorMode) {
      const widgetName = meta?.widgetName
        ?? resolveActivePreviewWidgetName()
        ?? (selectedKey?.startsWith("widget:") ? parseWidgetKey(selectedKey) : null);
      if (widgetName) {
        const spec = getWidgets(doc)?.[widgetName];
        const modes = widgetListPreviewModes(spec);
        let mode = null;
        if (show === "$base") {
          mode = modes.includes("data") ? "data" : null;
        } else if (modes.includes(show)) {
          mode = show;
        } else if (modes.includes("data")) {
          mode = "data";
        }
        if (mode) setWidgetPreviewMode(widgetName, mode);
        syncWidgetPreviewBar();
        paint({ refreshEditor: false });
      }
    }
    logAction(action, vars);
  };
}

function logAction(action, vars) {
  const eventName = action?.event || "action";
  const show = action?.show ? ` → ${action.show}` : "";
  const line = document.createElement("li");
  line.textContent = `${eventName}${show}`;
  actions.prepend(line);
}

function item(text) {
  const li = document.createElement("li");
  li.textContent = text;
  return li;
}

function loadWidgetDatasetSelection() {
  try {
    const raw = localStorage.getItem(WIDGET_DATASET_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function setWidgetDatasetKind(widgetName, kind) {
  if (!widgetName || kind == null) return;
  widgetDatasetSelection = { ...widgetDatasetSelection, [widgetName]: kind };
  try {
    localStorage.setItem(WIDGET_DATASET_STORAGE_KEY, JSON.stringify(widgetDatasetSelection));
  } catch {
    /* ignore */
  }
}

function getWidgetDatasetKind(widgetName, document) {
  const spec = getWidgets(document)?.[widgetName];
  const previewMode = getWidgetPreviewMode(widgetName, document);
  const kinds = listWidgetDatasetKinds(spec, previewMode);
  const stored = widgetDatasetSelection[widgetName];
  if (stored === "") return "";
  if (stored && kinds.includes(stored)) return stored;
  return kinds[0] ?? "";
}

function stateDatasetKind(widgetName) {
  const selected = getWidgetDatasetKind(widgetName, doc);
  if (selected) return selected;
  const spec = getWidgets(doc)?.[widgetName];
  const previewMode = getWidgetPreviewMode(widgetName, doc);
  return listWidgetDatasetKinds(spec, previewMode)[0] ?? "";
}

function getWidgetDatasetRecordId(widgetName, document) {
  return getWidgetDatasetKind(widgetName, document);
}

function currentDatasetState() {
  const widgetName = parseWidgetKey(selectedKey);
  if (!widgetName || !doc) return "";
  const kind = stateDatasetKind(widgetName);
  const table = doc.dataset?.[kind];
  const record = Array.isArray(table) ? table[0] : null;
  return typeof record?.state === "string" ? record.state : "";
}

function applyFirstRecordState(kind, state) {
  const table = doc?.dataset?.[kind];
  if (!Array.isArray(table) || !table[0] || typeof table[0] !== "object") return false;
  for (let index = 0; index < table.length; index += 1) {
    const record = table[index];
    if (!record || typeof record !== "object") continue;
    if (index === 0 && state) record.state = state;
    else delete record.state;
  }
  return true;
}

function loadWidgetPreviewModeSelection() {
  try {
    const raw = localStorage.getItem(WIDGET_PREVIEW_MODE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function setWidgetPreviewMode(widgetName, mode) {
  if (!widgetName || !mode) return;
  const spec = doc ? getWidgets(doc)[widgetName] : null;
  const allowed = widgetListPreviewModes(spec);
  if (!allowed.includes(mode)) return;
  widgetPreviewModeSelection = { ...widgetPreviewModeSelection, [widgetName]: mode };
  try {
    localStorage.setItem(WIDGET_PREVIEW_MODE_STORAGE_KEY, JSON.stringify(widgetPreviewModeSelection));
  } catch {
    /* ignore */
  }
}

function getWidgetPreviewMode(widgetName, document) {
  if (!widgetName) return "data";
  const spec = getWidgets(document)[widgetName];
  const modes = widgetListPreviewModes(spec);
  if (!modes.length) return "data";
  const stored = widgetPreviewModeSelection[widgetName];
  if (stored && modes.includes(stored)) return stored;
  return defaultWidgetPreviewMode(spec);
}

function walkWidgetSpecNodes(root, visit) {
  if (!root || typeof root !== "object") return;
  visit(root);
  for (const child of root.items ?? []) {
    walkWidgetSpecNodes(child, visit);
  }
}

function widgetSpecHasPreviewBehavior(spec) {
  if (!spec || typeof spec !== "object") return false;
  if (widgetHasListPhasePreview(spec)) return true;
  let found = false;
  const tag = (node) => {
    if (node?.interaction?.show) found = true;
  };
  if (spec.view) walkWidgetSpecNodes(spec.view, tag);
  if (Array.isArray(spec.phases)) {
    for (const phase of spec.phases) {
      for (const item of phase.items ?? []) walkWidgetSpecNodes(item, tag);
    }
  }
  return found;
}

function resolveActivePreviewWidgetName() {
  if (!doc) return null;
  const widgetFromKey = parseWidgetKey(previewScopeKey || selectedKey);
  if (widgetFromKey) return widgetFromKey;
  if (viewMode !== "component") return null;
  const surface = resolveChromePreviewPart(activeSurface());
  const catalogName = catalogNameForChromePart(doc, surface);
  if (catalogName && getWidgets(doc)[catalogName]?.view) return catalogName;
  return null;
}

function syncWidgetBehaviorToggle() {
  if (!widgetBehaviorWrap) return;
  const widgetName = resolveActivePreviewWidgetName();
  if (viewMode !== "component" || !widgetName) {
    widgetBehaviorWrap.hidden = true;
    widgetBehaviorMode = false;
    if (widgetBehaviorInput) widgetBehaviorInput.checked = false;
    return;
  }
  const spec = getWidgets(doc)[widgetName];
  const enabled = widgetSpecHasPreviewBehavior(spec);
  widgetBehaviorWrap.hidden = !enabled;
  if (!enabled) {
    widgetBehaviorMode = false;
    if (widgetBehaviorInput) widgetBehaviorInput.checked = false;
    return;
  }
  if (widgetBehaviorInput) widgetBehaviorInput.checked = widgetBehaviorMode;
}

function syncWidgetPreviewBar() {
  if (!widgetPreviewBar || !widgetDatasetSelect) return;
  const widgetName = selectedKey?.startsWith("widget:") ? parseWidgetKey(selectedKey) : null;
  if (viewMode !== "component" || !doc || !widgetName || isWidgetElementTreeKey(selectedKey)) {
    widgetPreviewBar.hidden = true;
    return;
  }
  const spec = getWidgets(doc)[widgetName];
  const listModes = widgetListPreviewModes(spec);
  if (!listModes.length) {
    widgetPreviewBar.hidden = true;
    return;
  }
  const previewMode = getWidgetPreviewMode(widgetName, doc);
  widgetPreviewBar.hidden = false;
  const modeHost = widgetPreviewBar.querySelector(".widget-preview-mode");
  if (modeHost) {
    modeHost.replaceChildren();
    for (const { mode, label } of listWidgetPreviewBarEntries(spec, listModes)) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "widget-preview-mode__btn";
      button.dataset.widgetPreviewMode = mode;
      button.textContent = label;
      const active = mode === previewMode;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      modeHost.append(button);
    }
  }
  widgetPreviewBar.classList.toggle(
    "is-skeleton",
    previewMode === "phase-skeleton" || previewMode === "skeleton" || previewMode === "data-skeleton",
  );
  const specKinds = listWidgetDatasetKinds(spec, previewMode);
  const datasetKind = getWidgetDatasetKind(widgetName, doc);
  const datasetLabel = widgetPreviewBar.querySelector(".widget-preview-dataset");
  if (datasetLabel) datasetLabel.hidden = false;
  widgetDatasetSelect.replaceChildren();
  for (const kind of specKinds) {
    const option = document.createElement("option");
    option.value = kind;
    option.textContent = kind;
    widgetDatasetSelect.append(option);
  }
  const empty = document.createElement("option");
  empty.value = "";
  empty.textContent = "— не задано";
  empty.setAttribute("aria-label", "Dataset не задан");
  widgetDatasetSelect.append(empty);
  widgetDatasetSelect.value = specKinds.includes(datasetKind) ? datasetKind : "";
}

async function persistDatasetSlice(kind) {
  const workspaceItem = currentAdminWorkspaceItem();
  const value = doc?.dataset?.[kind];
  if (!workspaceItem || value === undefined || !kind) return;
  try {
    const response = await fetch("/api/sdui-node", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        workspaceId: Number(workspaceItem.id),
        path: `/dataset/${kind}`,
        value,
      }),
    });
    const envelope = await response.json().catch(() => ({}));
    if (!response.ok) {
      errorBox.hidden = false;
      errorBox.textContent = envelope.error ?? `Сохранение dataset (${response.status})`;
      return;
    }
    errorBox.hidden = true;
    if (typeof envelope.version === "number") {
      updateWorkspaceVersionInCatalog(workspaceItem.id, envelope.version);
    }
  } catch (error) {
    errorBox.hidden = false;
    errorBox.textContent = `Сохранение dataset: ${error.message}`;
  }
}

function applyFrameBackground(color) {
  if (!color) return;
  document.documentElement.style.setProperty("--frame-preview-bg", color);
  if (frame) {
    frame.style.background = color;
  }
}

function restoreFrameBackground() {
  if (!frameBgInput) return;
  let color = DEFAULT_FRAME_BG;
  try {
    const stored = localStorage.getItem(FRAME_BG_STORAGE_KEY);
    if (stored && /^#[0-9a-f]{6}$/i.test(stored)) {
      color = stored;
    }
  } catch {
    /* ignore */
  }
  frameBgInput.value = color;
  applyFrameBackground(color);
}

function applyViewport() {
  const value = viewport.value;
  const fluid = value === "fluid";
  for (const el of [previewBlock, frame].filter(Boolean)) {
    el.classList.remove("is-fluid", "is-fixed");
    el.style.removeProperty("--preview-block-width");
    el.style.removeProperty("--frame-width");
    el.classList.add(fluid ? "is-fluid" : "is-fixed");
    if (!fluid) {
      el.style.setProperty("--preview-block-width", `${value}px`);
      el.style.setProperty("--frame-width", `${value}px`);
    }
  }
  paint();
  syncUrlFromAppState();
}

async function copyWithFeedback(button, text, feedbackLabel = "Скопировано") {
  await navigator.clipboard.writeText(text);
  if (button.querySelector("svg")) {
    const previousLabel = button.getAttribute("aria-label") || "Копировать";
    button.setAttribute("aria-label", feedbackLabel);
    button.classList.add("is-copied");
    setTimeout(() => {
      button.setAttribute("aria-label", previousLabel);
      button.classList.remove("is-copied");
    }, 1200);
    return;
  }
  const previous = button.textContent;
  button.textContent = feedbackLabel;
  setTimeout(() => { button.textContent = previous; }, 1200);
}

async function copyJson() {
  await copyWithFeedback(document.querySelector("#copy-json"), source.value);
}

const viewerDeps = {
  getDoc: () => {
    session.doc = doc;
    return doc;
  },
  getSelectedKey: () => {
    session.selectedKey = selectedKey;
    return selectedKey;
  },
  getPreviewScopeKey: () => previewScopeKey,
  getAdminImageBaseUrl: () => viewerConfig.adminApiBaseUrl || "",
  getTheme,
  getViewportWidth,
  activePageKey,
  activeSurface,
  mergePageVars,
  pageCanvas,
  errorBox,
  pageMeta,
  preview,
  previewBlock,
  meta,
  setComponentPreviewSurface,
  tree,
  source,
  createPreviewOnAction,
  previewSlotOptions,
  previewPhaseByWidget,
  clearPreviewBreakpointHint,
  resolveComponentPreviewPageKey,
  isValidComponentCard,
  resolveChromePreviewPart,
  CHROME_SURFACES_WITH_ELEMENTS,
  resolveNodeByKey,
  getWidgetPreviewMode,
  getWidgetDatasetRecordId,
  setRendered: (value) => { rendered = value; },
  updatePreviewBreakpointHint,
  getEditorProgrammatic: () => editorProgrammatic,
  setNodeByKey,
  paint,
  formatEditorJson,
  editorEditingCurrentSelection,
  updateNodeSaveButton,
  refreshEditorContractCheck,
  buildTreeBody,
  applyDefaultTreeCollapse,
  markTree,
  bumpLoadSampleGeneration: () => ++loadSampleGeneration,
  getLoadSampleGeneration: () => loadSampleGeneration,
  activeExampleFileName,
  contractApiUrl,
  ingestDocument,
  setDocFileName: (name) => { docFileName = name; },
  setDocSource: (value) => { docSource = value; },
  setDocFileHandle: (handle) => { docFileHandle = handle; },
  finishDocumentLoad,
  metaItem: item,
};

({ paintPage } = createPageStage(viewerDeps));
({ paintComponent } = createComponentStage(viewerDeps));
({ syncEditorToDoc, parseEditorJson } = createEditor(viewerDeps));
({ buildTree } = createStructure(viewerDeps));
({ loadSample } = createSource(viewerDeps));

bootstrapViewer().catch(showLoadError);
